import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import {
  workTaskApprove,
  workTaskBillingExport,
  workTaskTranslateToEnglish,
  workTaskLinkFeedback,
  workTaskList,
  workTaskSave,
  workTaskSetStatus,
  workTaskSetArchived,
  workTaskOrganize,
  workTaskSetBilling,
  workTaskSetPaid,
} from '../../db/work-task-resolvers.js';
import type {
  WorkTask,
  WorkTaskEstimate,
  WorkTaskHourRange,
  WorkTaskPriority,
  WorkTaskKind,
  WorkTaskFeedbackLink,
  WorkTaskListResult,
} from '../../db/work-task-types.js';
import { taskEstimate, parseTaskEstimate, estimateTotal, compareWorkTasks } from '../../db/work-task-estimates.js';
import {
  buildWorkTaskBillingReport,
  approvedWorkTaskMinutes,
  hasWorkTaskPayment,
  buildWorkTaskBillingSummary,
  validWorkTaskDate as isWorkTaskDate,
  type WorkTaskBillingReport,
} from '../../db/work-task-billing.js';
import type { WorkTaskEnglishTranslation } from '../../db/work-task-translation-types.js';
import { taskUiText, taskUiHours, taskUiCalendarDate, taskUiTimestamp, type TaskLanguage } from './work-task-i18n.js';
import { createTaskTranslationQueue } from './work-task-translation-client.js';
import { workTaskStyles } from './work-task-styles.js';
import { buildWorkTaskAnalysis, downloadWorkTaskAnalysis } from './work-task-analysis.js';

export type WorkTaskRequest = { mode: 'create' | 'link'; feedback: WorkTaskFeedbackLink; token: number };
type TaskView = 'active' | 'completed' | 'archived';
type BillingDraft = {
  taskID: string;
  expectedRevision: number;
  disposition: 'undecided' | 'non-billable' | 'billable';
  hours: string;
  minutes: string;
  source: string;
  agreedOn: string;
  billingDate: string;
};
const commercialMinutes = (task: WorkTask) => approvedWorkTaskMinutes(task) ?? 0;
const commercialContribution = (task: WorkTask) => buildWorkTaskBillingSummary([task]);
type TaskDraft = {
  _id?: string;
  expectedRevision?: number;
  taskNumber?: string;
  kind: WorkTaskKind;
  targetReleaseDate: string;
  billingHours: string;
  billingDate: string;
  title: string;
  description: string;
  testInstructions: string;
  assigneeUserID: string;
  developmentHours: string;
  testingHours: string;
  researchHours: string;
  researchMax: string;
  developmentMax: string;
  testingMax: string;
  totalMin: string;
  totalMax: string;
  estimateMode: WorkTaskEstimate['mode'];
  priority: WorkTaskPriority;
  pinned: boolean;
  deliveryEstimate: string;
  feedbackLinks: WorkTaskFeedbackLink[];
  wasApproved: boolean;
};
const STATUSES = [
  { value: 'raised', label: 'Raised' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'approved', label: 'Approved' },
  { value: 'in-implementation', label: 'In progress' },
  { value: 'implemented', label: 'Implemented' },
  { value: 'test-staging', label: 'Awaiting ZwickRoell testing' },
  { value: 'ready-for-production', label: 'Tested by ZwickRoell — Approved for release' },
  { value: 'production-completed', label: 'Completed in production' },
] as const;
const KINDS = [
  { value: 'unclassified', label: 'Noch nicht zugeordnet' },
  { value: 'bug', label: 'Bug · nicht kostenpflichtig' },
  { value: 'feature', label: 'Feature · Abrechnung separat' },
] as const;
const statusLabel = (status: string) => STATUSES.find(item => item.value === status)?.label ?? status;
const kindLabel = (kind: WorkTaskKind) => KINDS.find(item => item.value === kind)?.label ?? 'Noch nicht zugeordnet';
const currentMonth = () => {
  const now = new Date();
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return { from: `${month}-01`, to: `${month}-${new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()}` };
};
const PRIORITIES = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
] as const;
const estimateFields = (estimate: WorkTaskEstimate) => {
  const part = (name: 'research' | 'development' | 'testing') =>
    estimate.mode === 'breakdown' ? estimate[name] : { min: 0, max: 0 };
  const maximum = (range: WorkTaskHourRange) => (range.min === range.max ? '' : String(range.max));
  return {
    estimateMode: estimate.mode,
    researchHours: String(part('research').min),
    researchMax: maximum(part('research')),
    developmentHours: String(part('development').min),
    developmentMax: maximum(part('development')),
    testingHours: String(part('testing').min),
    testingMax: maximum(part('testing')),
    totalMin: estimate.mode === 'total' ? String(estimate.total.min) : '0',
    totalMax: estimate.mode === 'total' ? maximum(estimate.total) : '',
  };
};
const draftEstimate = (draft: TaskDraft): WorkTaskEstimate => {
  // An empty simple estimate is unknown, not a zero-hour commitment.
  if (draft.estimateMode === 'total' && !draft.totalMin.trim() && !draft.totalMax.trim()) return { mode: 'open' };
  const range = (min: string, max: string) => ({
    min: min.trim() ? Number(min.replace(',', '.')) : NaN,
    max: max.trim() || min.trim() ? Number((max.trim() || min).replace(',', '.')) : NaN,
  });
  return parseTaskEstimate(
    draft.estimateMode === 'open'
      ? { mode: 'open' }
      : draft.estimateMode === 'total'
        ? { mode: 'total', total: range(draft.totalMin, draft.totalMax) }
        : {
            mode: 'breakdown',
            research: range(draft.researchHours, draft.researchMax),
            development: range(draft.developmentHours, draft.developmentMax),
            testing: range(draft.testingHours, draft.testingMax),
          },
  );
};
const taskTotal = (task: WorkTask) => estimateTotal(taskEstimate(task));
const isApproved = (task: WorkTask) => !!task.approval && task.approval.estimateRevision === task.estimateRevision;
const canEstimateBeApproved = (task: WorkTask) =>
  !!task.description.trim() && (task.kind === 'bug' || (task.kind === 'feature' && (taskTotal(task)?.max ?? 0) > 0));
const parseJSON = <T>(raw: unknown): T => (typeof raw === 'string' ? JSON.parse(raw) : raw) as T;
const emptyDraft = (feedback?: WorkTaskFeedbackLink): TaskDraft => ({
  title: '',
  kind: 'unclassified',
  targetReleaseDate: '',
  billingHours: '',
  billingDate: '',
  description: '',
  testInstructions: '',
  assigneeUserID: '',
  ...estimateFields({ mode: 'total', total: { min: 0, max: 0 } }),
  totalMin: '',
  priority: 'medium',
  pinned: false,
  deliveryEstimate: '',
  feedbackLinks: feedback ? [feedback] : [],
  wasApproved: false,
});
const explainError = (error: unknown) => {
  const text = error instanceof Error ? error.message : String(error);
  if (text.includes('NotAuthorizedError'))
    return 'Die Sitzung ist abgelaufen oder Ihr Konto hat keine Berechtigung für diese Aktion. Bitte erneut anmelden oder den Zugriff prüfen. Ihre Eingaben bleiben erhalten.';
  if (text.includes('TASK_BILLING_UNPAID_ARCHIVE'))
    return 'Kostenpflichtige Tasks erst nach Zahlung archivieren. Archivierte Tasks vor einer Änderung der Zahlung oder offenen Abrechnung wiederherstellen.';
  if (text.includes('TASK_BILLING_PAID_LOCKED'))
    return 'Vor einer Änderung der Vereinbarung zuerst die Zahlungsmarkierung ausdrücklich aufheben.';
  if (text.includes('TASK_BILLING_AGREEMENT_REQUIRED'))
    return 'Zuerst eine kostenpflichtige Vereinbarung mit Stunden dokumentieren.';
  if (text.includes('TASK_INVALID_ASSIGNEE')) return 'Bitte wählen Sie ein aktives Benutzerkonto als Verantwortlichen.';
  if (text.includes('TASK_RELOAD_REQUIRED'))
    return 'Bitte laden Sie die Seite neu. Diese Planung verwendet neue Schätzfelder.';
  if (text.includes('TASK_CONFLICT'))
    return 'Dieser Task wurde zwischenzeitlich geändert. Laden Sie die Liste neu und öffnen Sie den aktuellen Task erneut. Ihre Eingaben bleiben bis dahin erhalten.';
  if (text.includes('TASK_FORBIDDEN')) return 'Ihr angemeldetes Konto hat keine Berechtigung für diese Aktion.';
  if (text.includes('TASK_ESTIMATE_REQUIRED'))
    return 'Für eine Freigabe werden eine Klassifikation und eine Beschreibung benötigt. Features benötigen zusätzlich eine Schätzung über 0 Stunden.';
  if (text.includes('TASK_CLASSIFICATION_REQUIRED'))
    return 'Bitte ordnen Sie den Task zuerst als Bug oder Feature ein.';
  if (text.includes('TASK_INVALID_BILLING_DATE_RANGE'))
    return 'Bitte wählen Sie einen gültigen Zeitraum. „Bis“ darf nicht vor „Von“ liegen.';
  if (text.includes('TASK_BILLING_EXPORT_TOO_LARGE'))
    return 'Der Bericht ist zu groß. Bitte wählen Sie einen kürzeren Zeitraum.';
  if (text.includes('TASK_FEEDBACK_NOT_FOUND'))
    return 'Dieses Feedback ist nicht mehr verfügbar. Bitte prüfen Sie den Eintrag in der Feedback-Liste.';
  if (text.includes('TASK_APPROVAL_REQUIRED'))
    return 'Die aktuelle Planung muss zuerst von Michael oder Herrn Brenner freigegeben werden.';
  if (text.includes('TASK_INVALID_INPUT'))
    return 'Bitte prüfen Sie die Angaben. Titel, Beschreibung und Stunden müssen gültig sein.';
  return 'Die Aktion konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut. Der bisherige Stand bleibt erhalten.';
};

export const WorkTasksPanel = stateful(
  ({ request, onRequestHandled }: { request: WorkTaskRequest | null; onRequestHandled: () => void }) => {
    const [data, setData] = useState<WorkTaskListResult | null>(null);
    const [language, setLanguage] = useState<TaskLanguage>('de');
    const [, setTranslationTick] = useState(0);
    const [translationQueue] = useState(
      createTaskTranslationQueue(
        async task =>
          parseJSON<WorkTaskEnglishTranslation>(await workTaskTranslateToEnglish(task._id, task.revision)()),
        () => setTranslationTick(Date.now() + Math.random()),
      ),
    );
    const t = (value: string) => taskUiText(language, value);
    const formatTaskHours = (value: WorkTaskHourRange | null) => taskUiHours(language, value);
    const calendarDate = (value: string | null | undefined) => taskUiCalendarDate(language, value);
    const date = (value: string | Date) => taskUiTimestamp(language, value);
    useEffect(() => {
      translationQueue.sync(language, data?.tasks ?? []);
    }, [language, data]);
    useEffect(() => () => translationQueue.dispose(), []);
    const translated = (task: WorkTask) => (language === 'en' ? translationQueue.get(task) : undefined);
    const viewTask = (task: WorkTask) => {
      const result = translated(task);
      return result?.phase === 'ready' && result.value
        ? {
            ...task,
            title: result.value.title,
            description: result.value.description,
            testInstructions: result.value.testInstructions ?? task.testInstructions ?? '',
            deliveryEstimate: result.value.deliveryEstimate,
            feedbackLinks: result.value.feedbackLinks,
          }
        : task;
    };

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [taskView, setTaskView] = useState<TaskView>('active');
    const [layout, setLayout] = useState<'board' | 'list'>('board');
    const [detailID, setDetailID] = useState<string | null>(null);
    const [billingDraft, setBillingDraft] = useState<BillingDraft | null>(null);
    const [billingFeedback, setBillingFeedback] = useState<{
      taskID: string;
      kind: 'saving' | 'error' | 'success';
      message: string;
    } | null>(null);
    const [ownerFilter, setOwnerFilter] = useState('all');
    const [releaseFilter, setReleaseFilter] = useState('all');
    const closeDetails = () => {
      (document.getElementById('zw-task-detail-dialog') as HTMLDialogElement | null)?.close();
      setDetailID(null);
      setBillingDraft(null);
      setBillingFeedback(null);
    };
    useEffect(() => {
      if (!detailID) return;
      queueMicrotask(() => {
        const dialog = document.getElementById('zw-task-detail-dialog') as HTMLDialogElement | null;
        if (dialog && !dialog.open) dialog.showModal();
      });
    }, [detailID]);
    const [archiveID, setArchiveID] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [approvalFilter, setApprovalFilter] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [kindFilter, setKindFilter] = useState('all');
    const [billingFrom, setBillingFrom] = useState(currentMonth().from);
    const [billingTo, setBillingTo] = useState(currentMonth().to);
    const [billingBusy, setBillingBusy] = useState(false);
    const [attachID, setAttachID] = useState<string | null>(null);
    const [attachValue, setAttachValue] = useState('');
    const [draft, setDraft] = useState<TaskDraft | null>(null);
    const [linkSource, setLinkSource] = useState<WorkTaskFeedbackLink | null>(null);
    const [linkTarget, setLinkTarget] = useState('');
    const [approveID, setApproveID] = useState<string | null>(null);
    const [approveName, setApproveName] = useState<'' | 'Michael' | 'Brenner'>('');
    const [exportProgress, setExportProgress] = useState<string | null>(null);
    const [exportText, setExportText] = useState<string | null>(null);
    const [actions] = useState({
      alive: true,
      busy: false,
      loadSequence: 0,
      requestToken: 0,
      exportBusy: false,
      exportCancelled: false,
      billingBusy: false,
    });

    const translationState = (task: WorkTask) => {
      if (language !== 'en') return nothing;
      const entry = translated(task);
      return entry?.phase === 'ready'
        ? html`<p class="zw-task-translation-ready">${t('Englische Ansicht · Originaldaten bleiben unverändert')}</p>`
        : html`<div class="zw-task-translation-state" role="status">
            <span
              >${t(
                entry?.phase === 'error'
                  ? 'Übersetzung nicht verfügbar. Originaltext wird angezeigt.'
                  : 'Englische Ansicht wird vorbereitet. Originaltext wird angezeigt.',
              )}</span
            >${entry?.phase === 'error'
              ? html`<button type="button" class="zw-task-button" @click=${() => translationQueue.retry(task)}>
                  ${t('Erneut übersetzen')}
                </button>`
              : nothing}
          </div>`;
    };
    const draftTranslation = (field: 'title' | 'description' | 'deliveryEstimate' | 'testInstructions') => {
      if (language !== 'en' || !draft?._id) return nothing;
      const task = data?.tasks.find(item => item._id === draft._id && item.revision === draft.expectedRevision);
      if (!task) return nothing;
      if (draft[field] !== task[field])
        return html`<p class="zw-task-translation-note">
          ${t('Übersetzung nicht mehr aktuell, da der Entwurf geändert wurde.')}
        </p>`;
      const entry = translated(task);
      if (entry?.phase !== 'ready' || !entry.value) return nothing;
      const original = (draft[field] ?? '').trim();
      const translation = (entry.value[field] ?? '').trim();
      if (!translation || translation === original || !/\p{L}/u.test(original)) return nothing;
      return html`<aside class="zw-task-field-translation">
        <small>${t('Englische Übersetzung · Original oben')}</small>
        <p>${entry.value[field] || '—'}</p>
      </aside>`;
    };
    const draftFeedback = () => {
      if (!draft) return [];
      const task = data?.tasks.find(item => item._id === draft._id && item.revision === draft.expectedRevision);
      const entry = task ? translated(task) : undefined;
      return entry?.phase === 'ready' && entry.value ? entry.value.feedbackLinks : draft.feedbackLinks;
    };

    const copyAnalysis = async (task?: WorkTask) => {
      if (actions.exportBusy || draft || !data) return;
      actions.exportBusy = true;
      actions.exportCancelled = false;
      setExportText(null);
      setExportProgress('Analysepaket wird zusammengestellt…');
      setError(null);
      setNotice(null);
      try {
        const result = await buildWorkTaskAnalysis({
          task,
          origin: window.location.origin,
          progress: text => {
            if (actions.alive) setExportProgress(text);
          },
          cancelled: () => !actions.alive || actions.exportCancelled,
        });
        if (!actions.alive || actions.exportCancelled) return;
        setExportText(result.text);
        const warning = result.warnings ? ` ${result.warnings} Abschnitte fehlen und sind im Text markiert.` : '';
        try {
          await navigator.clipboard.writeText(result.text);
          if (actions.alive) setNotice('Analysepaket kopiert.' + warning);
        } catch {
          if (actions.alive) {
            closeDetails();
            setNotice('Analysepaket bereit. Bitte „Text kopieren“ oder „Als TXT speichern“ wählen.' + warning);
          }
        }
      } catch (cause) {
        if (actions.alive) {
          if (String(cause).includes('TASK_EXPORT_CANCELLED')) setNotice('Export abgebrochen.');
          else setError('Analysepaket konnte nicht erstellt werden. Bitte erneut versuchen.');
        }
      } finally {
        actions.exportBusy = false;
        if (actions.alive) setExportProgress(null);
      }
    };

    const loadTasks = async () => {
      const sequence = ++actions.loadSequence;
      setLoading(true);
      setError(null);
      try {
        const result = parseJSON<WorkTaskListResult>(await workTaskList()());
        if (!Array.isArray(result?.tasks) || !result?.access) throw new Error('INVALID_TASK_LIST');
        if (actions.alive && sequence === actions.loadSequence) setData(result);
      } catch {
        if (actions.alive && sequence === actions.loadSequence)
          setError('Tasks konnten nicht geladen werden. Bitte erneut laden. Ihr Feedback ist weiterhin verfügbar.');
      } finally {
        if (actions.alive && sequence === actions.loadSequence) setLoading(false);
      }
    };
    useEffect(() => {
      actions.alive = true;
      void loadTasks();
      return () => {
        actions.alive = false;
        actions.loadSequence++;
      };
    }, []);

    useEffect(() => {
      if (!request || !data || request.token === actions.requestToken || actions.busy) return;
      actions.requestToken = request.token;
      setNotice(null);
      setError(null);
      if (!data.access.canManage) {
        setError('Nur die Projektverantwortlichen können Tasks erstellen und Feedback verknüpfen.');
      } else if (request.mode === 'create') {
        if (draft?._id) {
          setError(
            t(
              'Bitte speichern oder schließen Sie zuerst die offene Bearbeitung. Erstellen Sie den neuen Task anschließend erneut über das Feedback.',
            ),
          );
        } else {
          setTaskView('active');
          setStatusFilter('all');
          setArchiveID(null);
          const next = draft ?? emptyDraft();
          setDraft({
            ...next,
            feedbackLinks: next.feedbackLinks.some(source => source.callID === request.feedback.callID)
              ? next.feedbackLinks
              : [...next.feedbackLinks, request.feedback],
          });
          setLinkSource(null);
          setApproveID(null);
        }
      } else {
        setLinkSource(request.feedback);
        setLinkTarget('');
        setApproveID(null);
      }
      onRequestHandled();
    }, [request?.token, data?.access.canManage, busy]);

    const tasks = [...(data?.tasks ?? [])].sort(compareWorkTasks);
    const openTasks = tasks.filter(task => !task.archivedAt && task.status !== 'production-completed');
    const completedTasks = tasks.filter(task => !task.archivedAt && task.status === 'production-completed');
    const archivedTasks = tasks.filter(task => !!task.archivedAt);
    // Release approval is not production completion. Both remain explicit stages on the board.
    const viewTasks =
      taskView === 'active'
        ? layout === 'board'
          ? tasks.filter(task => !task.archivedAt)
          : openTasks
        : taskView === 'completed'
          ? completedTasks
          : archivedTasks;
    const selectTaskView = (view: TaskView) => {
      if (actions.busy || draft || billingDraft) return;
      setTaskView(view);
      closeDetails();
      setStatusFilter('all');
      setArchiveID(null);
      setApproveID(null);
      setAttachID(null);
    };
    const billingSummary = data?.billingSummary;
    const formatMinutes = (value: number) => `${Math.floor(value / 60)} h ${value % 60} min`;
    const billingLabel = (task: WorkTask) =>
      t(
        task.kind === 'bug' || task.billingDisposition === 'non-billable'
          ? 'Nicht kostenpflichtig'
          : task.billingDisposition === 'billable'
            ? 'Vereinbart kostenpflichtig'
            : 'Abrechnung noch offen',
      );
    const canManage = data?.access.canManage === true;
    const canApprove = data?.access.canApprove === true;
    const owners = new Map((data?.assignees ?? []).map(owner => [owner.userID, owner.name]));
    for (const task of tasks)
      if (task.assignee && !owners.has(task.assignee.userID)) owners.set(task.assignee.userID, task.assignee.name);
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const nextAction = (task: WorkTask) => {
      if (task.archivedAt) return t('Archiviert');
      if (task.status === 'production-completed') return t('In Produktion abgeschlossen');
      if (task.status === 'ready-for-production') return t('Nächster Schritt: Freigegebene Version bereitstellen');
      if (task.status === 'test-staging') return t('Nächster Schritt: Staging-Test durch ZwickRoell');
      if (task.status === 'implemented') return t('Nächster Schritt: Staging-Test vorbereiten');
      return (task.status === 'raised' || task.status === 'acknowledged') && !canEstimateBeApproved(task)
        ? t('Nächster Schritt: Planung ergänzen')
        : t('Nächster Schritt: Umsetzung');
    };
    const shown = viewTasks.filter(task => {
      const term = search.trim().toLocaleLowerCase('de');
      return (
        (!term ||
          `${task.taskNumber} ${task.title} ${task.description} ${task.testInstructions ?? ''} ${task.assignee?.name ?? ''} ${viewTask(task).title} ${viewTask(task).description} ${task.feedbackLinks.map(source => `${source.testerName} ${source.callID}`).join(' ')}`
            .toLocaleLowerCase('de')
            .includes(term)) &&
        (statusFilter === 'all' || task.status === statusFilter) &&
        (ownerFilter === 'all' ||
          (ownerFilter === 'mine'
            ? !!data?.access.userID && task.assignee?.userID === data.access.userID
            : ownerFilter === 'unassigned'
              ? !task.assignee
              : task.assignee?.userID === ownerFilter)) &&
        (releaseFilter === 'all' ||
          (releaseFilter === 'unset'
            ? !task.targetReleaseDate
            : !!task.targetReleaseDate &&
              task.targetReleaseDate < today &&
              task.status !== 'production-completed' &&
              !task.archivedAt)) &&
        (kindFilter === 'all' || task.kind === kindFilter) &&
        (priorityFilter === 'all' || (task.priority ?? 'medium') === priorityFilter) &&
        (approvalFilter === 'all' ||
          (approvalFilter === 'approved' ? isApproved(task) : task.kind === 'feature' && !isApproved(task)))
      );
    });
    const replaceTask = (task: WorkTask) => {
      if (!task?._id || !data) throw new Error('INVALID_TASK_RESPONSE');
      const previous = data.tasks.find(existing => existing._id === task._id);
      const before = previous
        ? commercialContribution(previous)
        : { approvedMinutes: 0, paidMinutes: 0, unpaidMinutes: 0, billableTasks: 0, undecidedTasks: 0 };
      const after = commercialContribution(task);
      // Apply only this mutation's delta to the server's full-database summary, even if the list is truncated.
      const summary = data.billingSummary ? { ...data.billingSummary } : undefined;
      if (summary)
        for (const key of Object.keys(after) as (keyof typeof after)[]) summary[key] += after[key] - before[key];
      setData({
        ...data,
        ...(summary ? { billingSummary: summary } : {}),
        tasks: [task, ...data.tasks.filter(existing => existing._id !== task._id)].sort(compareWorkTasks),
      });
    };
    const mutate = async (
      id: string,
      action: () => Promise<unknown>,
      message: string,
      completed?: () => void,
      failed?: (message: string) => void,
    ) => {
      if (actions.busy) return;
      actions.busy = true;
      actions.loadSequence++;
      setBusy(id);
      setLoading(false);
      setError(null);
      setNotice(null);
      try {
        const task = parseJSON<WorkTask>(await action());
        if (!actions.alive) return;
        replaceTask(task);
        completed?.();
        setNotice(message);
      } catch (cause) {
        if (actions.alive) {
          const message = explainError(cause);
          setError(message);
          failed?.(message);
        }
      } finally {
        actions.busy = false;
        if (actions.alive) setBusy(null);
      }
    };
    const updateDraft = (
      field:
        | 'title'
        | 'description'
        | 'testInstructions'
        | 'assigneeUserID'
        | 'researchHours'
        | 'researchMax'
        | 'developmentHours'
        | 'developmentMax'
        | 'testingHours'
        | 'testingMax'
        | 'totalMin'
        | 'totalMax'
        | 'deliveryEstimate'
        | 'targetReleaseDate'
        | 'billingHours'
        | 'billingDate',
      value: string,
    ) => {
      if (draft) setDraft({ ...draft, [field]: value });
    };
    const openDraft = (task?: WorkTask) => {
      if (actions.busy || draft || billingDraft) return;
      setError(null);
      setNotice(null);
      setApproveID(null);
      setArchiveID(null);
      closeDetails();
      if (!task) selectTaskView('active');
      setDraft(
        task
          ? {
              _id: task._id,
              expectedRevision: task.revision,
              title: task.title,
              taskNumber: task.taskNumber,
              kind: task.kind ?? 'unclassified',
              targetReleaseDate: task.targetReleaseDate ?? '',
              billingHours: task.billingHours == null ? '' : String(task.billingHours),
              billingDate: task.billingDate ?? '',
              description: task.description,
              testInstructions: task.testInstructions ?? '',
              assigneeUserID: task.assignee?.userID ?? '',
              ...estimateFields(taskEstimate(task)),
              priority: task.priority ?? 'medium',
              pinned: task.pinned === true,
              deliveryEstimate: task.deliveryEstimate,
              feedbackLinks: task.feedbackLinks,
              wasApproved: isApproved(task),
            }
          : emptyDraft(),
      );
      queueMicrotask(() =>
        document.getElementById('zw-task-editor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      );
    };
    const saveDraft = (event: Event) => {
      event.preventDefault();
      if (!draft || actions.busy || !canManage) return;
      const form = event.currentTarget as HTMLFormElement | null;
      // Native validation cannot focus a field inside a closed details section.
      if (form?.checkValidity && !form.checkValidity()) {
        for (const input of form.querySelectorAll(':invalid')) {
          let section = input.closest('details');
          while (section) {
            section.open = true;
            section = section.parentElement?.closest('details') ?? null;
          }
        }
        form.reportValidity();
        return;
      }
      let estimate: WorkTaskEstimate;
      try {
        estimate = draftEstimate(draft);
        if (!draft.title.trim()) throw new Error();
        if (
          (!draft._id && draft.kind === 'unclassified') ||
          (draft.targetReleaseDate && !isWorkTaskDate(draft.targetReleaseDate)) ||
          (draft.kind === 'feature' && draft.billingDate && !isWorkTaskDate(draft.billingDate))
        )
          throw new Error();
        if (draft.kind === 'feature' && draft.billingHours.trim()) {
          const hours = Number(draft.billingHours.replace(',', '.'));
          if (
            !Number.isFinite(hours) ||
            hours < 0 ||
            hours > 2000 ||
            Math.abs(hours * 100 - Math.round(hours * 100)) > 1e-8
          )
            throw new Error();
        }
      } catch {
        form?.querySelectorAll?.('details').forEach(section => {
          section.open = true;
        });
        setError(
          t(
            'Bitte prüfen Sie Titel, Klassifikation, Stunden und Datum. Das Stundenmaximum darf nicht unter dem Minimum liegen.',
          ),
        );
        return;
      }
      const payload = {
        ...(draft._id
          ? { _id: draft._id, expectedRevision: draft.expectedRevision }
          : { feedbackCallIDs: draft.feedbackLinks.map(source => source.callID) }),
        title: draft.title.trim(),
        description: draft.description.trim(),
        testInstructions: draft.testInstructions.trim(),
        assigneeUserID: draft.assigneeUserID || null,
        estimate,
        kind: draft.kind,
        targetReleaseDate: draft.targetReleaseDate || null,
        billingHours:
          draft.kind === 'feature' && draft.billingHours.trim() ? Number(draft.billingHours.replace(',', '.')) : null,
        billingDate: draft.kind === 'feature' ? draft.billingDate || null : null,
        priority: draft.priority,
        pinned: draft.pinned,
        deliveryEstimate: draft.deliveryEstimate.trim(),
      };
      void mutate(
        draft._id ?? 'new',
        () => workTaskSave(JSON.stringify(payload))(),
        t('Task gespeichert.'),
        () => setDraft(null),
      );
    };
    const submitLink = () => {
      const task = tasks.find(item => item._id === linkTarget);
      if (!task || !linkSource || actions.busy) return;
      void mutate(
        task._id,
        () => workTaskLinkFeedback(task._id, linkSource.callID, task.revision)(),
        t('Feedback mit dem Task verknüpft.'),
        () => {
          setLinkSource(null);
          setLinkTarget('');
        },
      );
    };
    const changeStatus = (task: WorkTask, event: Event) => {
      const select = event.currentTarget as HTMLSelectElement;
      const status = select.value;
      select.value = task.status;
      if (status === task.status || actions.busy || draft || billingDraft || !canManage) return;
      if (status === 'approved' && !isApproved(task)) {
        if (!canApprove) {
          setError('Ihr Konto kann keine Freigabe dokumentieren.');
          return;
        }
        if (!canEstimateBeApproved(task)) {
          setError(explainError('TASK_ESTIMATE_REQUIRED'));
          return;
        }
        setApproveID(task._id);
        setApproveName('');
        setError(null);
        return;
      }
      void mutate(
        task._id,
        () => workTaskSetStatus(task._id, status, task.revision)(),
        t(
          status === 'production-completed'
            ? 'Produktion bestätigt. Der Task steht jetzt unter „Abgeschlossen“.'
            : status === 'ready-for-production'
              ? 'Zur Veröffentlichung freigegeben. Die Produktion ist noch nicht bestätigt.'
              : 'Status aktualisiert.',
        ),
        () => setArchiveID(null),
      );
    };
    const setArchived = (task: WorkTask, archived: boolean) => {
      if (!canManage || actions.busy || draft || billingDraft) return;
      if (archived && commercialMinutes(task) && !hasWorkTaskPayment(task)) {
        setError(explainError('TASK_BILLING_UNPAID_ARCHIVE'));
        return;
      }
      void mutate(
        task._id,
        () => workTaskSetArchived(task._id, archived, task.revision)(),
        t(
          archived
            ? 'Task archiviert. Im Archiv können Sie ihn jederzeit wiederherstellen.'
            : task.status === 'production-completed'
              ? 'Task wiederhergestellt. Sie finden ihn unter „Abgeschlossen“.'
              : 'Task wiederhergestellt. Sie finden ihn unter „Aktiv“.',
        ),
        () => {
          setArchiveID(null);
          setApproveID(null);
          setAttachID(null);
        },
      );
    };
    const organize = (task: WorkTask, priority = task.priority ?? 'medium', pinned = task.pinned === true) => {
      if (!canManage || actions.busy || draft || billingDraft) return;
      void mutate(
        task._id,
        () => workTaskOrganize(task._id, priority, pinned, task.revision)(),
        t('Sortierung gespeichert.'),
      );
    };
    const attachFeedback = (task: WorkTask, event: Event) => {
      event.preventDefault();
      if (!canManage || actions.busy || draft || billingDraft) return;
      let callID = attachValue.trim();
      try {
        if (/^https?:\/\//i.test(callID)) {
          const url = new URL(callID);
          if (url.origin !== window.location.origin || !/^\/calls\/[^/]+\/?$/.test(url.pathname)) throw new Error();
          callID = decodeURIComponent(url.pathname.split('/')[2]!);
        }
        if (!callID || callID.length > 256 || /[\s/]/.test(callID)) throw new Error();
      } catch {
        setError('Bitte eine Anruf-ID oder einen Anruf-Link aus diesem System eingeben.');
        return;
      }
      void mutate(
        task._id,
        () => workTaskLinkFeedback(task._id, callID, task.revision)(),
        t('Feedback mit dem Task verknüpft.'),
        () => {
          setAttachID(null);
          setAttachValue('');
        },
      );
    };
    const sourceCard = (source: WorkTaskFeedbackLink) =>
      html`<div class="zw-task-source">
        <span class="zw-task-source-icon" aria-hidden="true">↗</span>
        <span
          ><strong>${source.testerName || 'Feedback'}</strong>
          <span class="zw-task-source-preview"
            >${source.notes || source.notesPreview || t('Verknüpftes Feedback zur ursprünglichen Beobachtung.')}</span
          >
          <small
            >${source.notes ? t('Feedback zum Zeitpunkt der Verknüpfung') : t('Feedback-Auszug')}${source.notesTruncated
              ? t(' · gekürzt')
              : ''}</small
          >
          <a
            class="zw-task-source-link"
            href=${`/calls/${encodeURIComponent(source.callID)}`}
            target="_blank"
            rel="noopener noreferrer"
            >${t('Anruf')} ${source.callID} ${t('öffnen ↗')}</a
          >
        </span>
      </div>`;
    const hourInput = (
      id: string,
      label: string,
      minField: 'researchHours' | 'developmentHours' | 'testingHours' | 'totalMin',
      maxField: 'researchMax' | 'developmentMax' | 'testingMax' | 'totalMax',
    ) =>
      draft
        ? html` <fieldset class="zw-task-range">
            <legend>${t(label)}</legend>
            <div>
              <label for=${`zw-task-${id}`}
                >${t('Stunden / von')}<input
                  id=${`zw-task-${id}`}
                  type="number"
                  min="0"
                  max="2000"
                  step="0.01"
                  ?required=${draft.estimateMode === 'breakdown'}
                  inputmode="decimal"
                  placeholder=${t('Noch nicht geschätzt')}
                  .value=${draft[minField]}
                  ?disabled=${busy !== null}
                  @input=${(event: Event) => updateDraft(minField, (event.currentTarget as HTMLInputElement).value)}
              /></label>
              <details class="zw-task-range-extra" ?open=${!!draft[maxField].trim()}>
                <summary>${t('Spanne hinzufügen (optional)')}</summary>
                <label for=${`zw-task-${id}-max`}
                  >${t('Bis (optional)')}<input
                    id=${`zw-task-${id}-max`}
                    type="number"
                    min="0"
                    max="2000"
                    step="0.01"
                    inputmode="decimal"
                    placeholder=${t('gleicher Wert')}
                    .value=${draft[maxField]}
                    ?disabled=${busy !== null}
                    @input=${(event: Event) => updateDraft(maxField, (event.currentTarget as HTMLInputElement).value)}
                /></label>
              </details>
            </div>
          </fieldset>`
        : nothing;
    const estimateDetails = (task: WorkTask) => {
      const estimate = taskEstimate(task);
      return estimate.mode === 'breakdown'
        ? html`<dl>
            ${(['research', 'development', 'testing'] as const).map(
              (key, i) =>
                html`<div>
                  <dt>${[t('Recherche'), t('Entwicklung'), 'Testing'][i]}</dt>
                  <dd>${formatTaskHours(estimate[key])}</dd>
                </div>`,
            )}
          </dl>`
        : html`<p class="zw-task-help">
            ${estimate.mode === 'open'
              ? t('Recherche und Budget noch abzustimmen. Nicht in der Stundensumme enthalten.')
              : t('Gesamter Umfang inklusive Testing. Aufteilung siehe Beschreibung.')}
          </p>`;
    };
    const hoursSummaryMetric = () =>
      html`<div class="zw-task-hours-metric" data-summary=${'billable'}>
        <span>${t('Offene abrechenbare Stunden')}</span>
        <strong>${billingSummary ? formatMinutes(billingSummary.unpaidMinutes) : '—'}</strong>
        <small>${t('Alle Tasks · unabhängig von Status und Filtern')}</small>
        ${billingSummary
          ? html`<div class="zw-task-payment-totals">
              <span>${t('Vereinbart')}: <b>${formatMinutes(billingSummary.approvedMinutes)}</b></span>
              <span>${t('Bezahlt')}: <b>${formatMinutes(billingSummary.paidMinutes)}</b></span>
            </div>`
          : nothing}
      </div>`;
    const openBilling = (task: WorkTask) => {
      if (!canManage || actions.busy || draft || billingDraft) return;
      const minutes = commercialMinutes(task);
      setBillingFeedback(null);
      setNotice(null);
      setBillingDraft({
        taskID: task._id,
        expectedRevision: task.revision,
        disposition: task.kind === 'bug' ? 'non-billable' : (task.billingDisposition ?? 'undecided'),
        hours: String(Math.floor(minutes / 60)),
        minutes: String(minutes % 60),
        source: task.billingAgreement?.source ?? '',
        agreedOn: task.billingAgreement?.agreedOn ?? '',
        billingDate: task.billingDate ?? '',
      });
      setError(null);
    };
    const saveBilling = (event: Event) => {
      event.preventDefault();
      if (!billingDraft || !canManage || actions.busy) return;
      const failBilling = (message: string) => {
        setNotice(null);
        setError(message);
        setBillingFeedback({ taskID: billingDraft.taskID, kind: 'error', message });
      };
      const current = tasks.find(task => task._id === billingDraft.taskID);
      const hours = Number(billingDraft.hours);
      const minutes = Number(billingDraft.minutes);
      const approvedMinutes = billingDraft.disposition === 'billable' ? hours * 60 + minutes : 0;
      if (!current || current.revision !== billingDraft.expectedRevision) {
        failBilling(explainError('TASK_CONFLICT'));
        return;
      }
      if (
        (billingDraft.disposition === 'billable' &&
          (!/^\d+$/.test(billingDraft.hours) ||
            !/^\d+$/.test(billingDraft.minutes) ||
            !Number.isSafeInteger(approvedMinutes) ||
            approvedMinutes <= 0 ||
            approvedMinutes > 120000 ||
            minutes > 59)) ||
        (billingDraft.disposition !== 'undecided' && !billingDraft.source.trim()) ||
        (billingDraft.agreedOn && !isWorkTaskDate(billingDraft.agreedOn)) ||
        (billingDraft.billingDate && !isWorkTaskDate(billingDraft.billingDate))
      ) {
        failBilling('Bitte ganze Stunden und Minuten (0–59) sowie den Beleg zur Vereinbarung eintragen.');
        return;
      }
      if (
        hasWorkTaskPayment(current) &&
        (billingDraft.disposition !== current.billingDisposition ||
          approvedMinutes !== commercialMinutes(current) ||
          billingDraft.source.trim() !== current.billingAgreement?.source ||
          billingDraft.agreedOn !== (current.billingAgreement?.agreedOn ?? ''))
      ) {
        failBilling(explainError('TASK_BILLING_PAID_LOCKED'));
        return;
      }
      if (current.archivedAt && billingDraft.disposition === 'billable' && !hasWorkTaskPayment(current)) {
        failBilling(explainError('TASK_BILLING_UNPAID_ARCHIVE'));
        return;
      }
      const successMessage = hasWorkTaskPayment(current)
        ? 'Abrechnungsdatum gespeichert.'
        : 'Abrechnungsvereinbarung gespeichert.';
      setBillingFeedback({ taskID: current._id, kind: 'saving', message: 'Speichert…' });
      void mutate(
        current._id,
        () =>
          workTaskSetBilling(
            current._id,
            billingDraft.expectedRevision,
            billingDraft.disposition,
            approvedMinutes,
            billingDraft.source.trim(),
            billingDraft.agreedOn,
            billingDraft.billingDate,
          )(),
        t(successMessage),
        () => {
          setBillingDraft(null);
          setBillingFeedback({ taskID: current._id, kind: 'success', message: successMessage });
        },
        failBilling,
      );
    };
    const setPaid = (task: WorkTask, paid: boolean) => {
      if (!canManage || actions.busy || draft || billingDraft || !commercialMinutes(task)) return;
      if (task.archivedAt && !paid) {
        setError(explainError('TASK_BILLING_UNPAID_ARCHIVE'));
        return;
      }
      void mutate(
        task._id,
        () => workTaskSetPaid(task._id, paid, task.revision)(),
        t(paid ? 'Zahlung ausdrücklich bestätigt.' : 'Zahlungsmarkierung aufgehoben.'),
      );
    };
    const billingStatus = (task: WorkTask) =>
      billingFeedback?.taskID === task._id
        ? html`<p
            class="zw-task-billing-feedback"
            data-feedback=${billingFeedback.kind}
            role=${billingFeedback.kind === 'error' ? 'alert' : 'status'}
            aria-live=${billingFeedback.kind === 'error' ? 'assertive' : 'polite'}
          >
            ${t(billingFeedback.message)}
          </p>`
        : nothing;
    const billingSection = (task: WorkTask) =>
      html`<section class="zw-task-commercial" aria-label=${t('Abrechnungsvereinbarung')}>
        <div class="zw-task-commercial-heading">
          <div>
            <h3>${t('Abrechnungsvereinbarung')}</h3>
            <strong
              data-billing-disposition=${task.kind === 'bug'
                ? 'non-billable'
                : (task.billingDisposition ?? 'undecided')}
              >${billingLabel(task)}</strong
            >
          </div>
          <strong class="zw-task-commercial-total"
            >${task.kind === 'bug' || task.billingDisposition === 'non-billable'
              ? formatMinutes(0)
              : commercialMinutes(task)
                ? formatMinutes(commercialMinutes(task))
                : '—'}</strong
          >
        </div>
        <p class="zw-task-help">
          ${t('Arbeitsaufwand und Freigabe sind keine Abrechnungsvereinbarung. Kostenlose Features bleiben Features.')}
        </p>
        ${task.billingAgreement
          ? html`<p class="zw-task-commercial-source">
              ${task.billingAgreement.source}
              <small
                >${task.billingAgreement.agreedOn ? calendarDate(task.billingAgreement.agreedOn) + ' · ' : ''}${t(
                  'Erfasst am',
                )}
                ${date(task.billingAgreement.recordedAt)}</small
              >
            </p>`
          : nothing}
        ${commercialMinutes(task) > 0
          ? html`<label class="zw-task-paid-option"
                ><input
                  type="checkbox"
                  data-action="mark-paid"
                  .checked=${!!hasWorkTaskPayment(task)}
                  ?disabled=${!canManage ||
                  busy !== null ||
                  draft !== null ||
                  billingDraft !== null ||
                  !!task.archivedAt}
                  @change=${(event: Event) => setPaid(task, (event.currentTarget as HTMLInputElement).checked)}
                />${t('Bezahlt · nur nach tatsächlichem Zahlungseingang markieren')}</label
              >
              ${hasWorkTaskPayment(task)
                ? html`<p class="zw-task-help">${t('Zahlung erfasst am')} ${date(task.payment!.paidAt)}</p>`
                : nothing}
              ${task.archivedAt
                ? html`<p class="zw-task-help">${t('Vor einer Änderung der Zahlung den Task wiederherstellen.')}</p>`
                : nothing}`
          : nothing}
        ${billingDraft?.taskID === task._id
          ? html`<form class="zw-task-commercial-editor" @submit=${saveBilling}>
              <label for="zw-task-agreement-disposition"
                >${t('Abrechnung')}<select
                  id="zw-task-agreement-disposition"
                  ?disabled=${busy !== null || task.kind === 'bug' || hasWorkTaskPayment(task)}
                  @change=${(event: Event) =>
                    setBillingDraft({
                      ...billingDraft,
                      disposition: (event.currentTarget as HTMLSelectElement).value as BillingDraft['disposition'],
                    })}
                >
                  ${[
                    ['undecided', 'Abrechnung noch offen'],
                    ['non-billable', 'Nicht kostenpflichtig'],
                    ['billable', 'Vereinbart kostenpflichtig'],
                  ].map(
                    ([value, label]) =>
                      html`<option value=${value} .selected=${billingDraft.disposition === value}>
                        ${t(label!)}
                      </option>`,
                  )}
                </select></label
              >
              ${billingDraft.disposition === 'billable'
                ? html`<div class="zw-task-agreement-duration">
                    <label for="zw-task-agreement-hours"
                      >${t('Stunden')}<input
                        id="zw-task-agreement-hours"
                        type="number"
                        min="0"
                        max="2000"
                        step="1"
                        inputmode="numeric"
                        required
                        .value=${billingDraft.hours}
                        ?disabled=${busy !== null || hasWorkTaskPayment(task)}
                        @input=${(event: Event) =>
                          setBillingDraft({ ...billingDraft, hours: (event.currentTarget as HTMLInputElement).value })}
                    /></label>
                    <label for="zw-task-agreement-minutes"
                      >${t('Minuten')}<input
                        id="zw-task-agreement-minutes"
                        type="number"
                        min="0"
                        max="59"
                        step="1"
                        inputmode="numeric"
                        required
                        .value=${billingDraft.minutes}
                        ?disabled=${busy !== null || hasWorkTaskPayment(task)}
                        @input=${(event: Event) =>
                          setBillingDraft({
                            ...billingDraft,
                            minutes: (event.currentTarget as HTMLInputElement).value,
                          })}
                    /></label>
                  </div>`
                : nothing}
              <label for="zw-task-agreement-source"
                >${t('Vereinbarung / Beleg')}<input
                  id="zw-task-agreement-source"
                  maxlength="2000"
                  placeholder=${t('z. B. mit Michael vereinbart, E-Mail vom …')}
                  .value=${billingDraft.source}
                  ?required=${billingDraft.disposition !== 'undecided'}
                  ?disabled=${busy !== null || hasWorkTaskPayment(task)}
                  @input=${(event: Event) =>
                    setBillingDraft({ ...billingDraft, source: (event.currentTarget as HTMLInputElement).value })}
              /></label>
              <label for="zw-task-agreement-date"
                >${t('Vereinbart am (optional)')}<input
                  id="zw-task-agreement-date"
                  type="date"
                  .value=${billingDraft.agreedOn}
                  ?disabled=${busy !== null || hasWorkTaskPayment(task)}
                  @input=${(event: Event) =>
                    setBillingDraft({ ...billingDraft, agreedOn: (event.currentTarget as HTMLInputElement).value })}
              /></label>
              <details class="zw-task-report-date" ?open=${hasWorkTaskPayment(task)}>
                <summary>${t('Abrechnungsdatum für PDF (optional)')}</summary>
                <label for="zw-task-agreement-billing-date"
                  >${t('Abrechnungsdatum')}<input
                    id="zw-task-agreement-billing-date"
                    type="date"
                    .value=${billingDraft.billingDate}
                    ?disabled=${busy !== null}
                    @input=${(event: Event) =>
                      setBillingDraft({
                        ...billingDraft,
                        billingDate: (event.currentTarget as HTMLInputElement).value,
                      })}
                /></label>
                <p class="zw-task-help">
                  ${t(
                    'Dieses Datum ordnet den Task im PDF-Bericht einem Zeitraum zu; es bestätigt keine Rechnung oder Zahlung.',
                  )}
                </p>
              </details>
              <p class="zw-task-help">
                ${t('Nur eine tatsächlich getroffene Vereinbarung erfassen. Speichern markiert nichts als bezahlt.')}
              </p>
              ${billingStatus(task)}
              <div class="zw-task-confirm-actions">
                <button
                  type="button"
                  class="zw-task-button"
                  ?disabled=${busy !== null}
                  @click=${() => {
                    setBillingDraft(null);
                    setBillingFeedback(null);
                  }}
                >
                  ${t('Abbrechen')}
                </button>
                <button type="submit" class="zw-task-button zw-task-primary" ?disabled=${busy !== null}>
                  ${t(hasWorkTaskPayment(task) ? 'Abrechnungsdatum speichern' : 'Vereinbarung speichern')}
                </button>
              </div>
            </form>`
          : canManage && task.kind !== 'unclassified'
            ? html`<button
                type="button"
                class="zw-task-button"
                data-action="edit-billing"
                ?disabled=${busy !== null || draft !== null || billingDraft !== null}
                @click=${() => openBilling(task)}
              >
                ${t(hasWorkTaskPayment(task) ? 'Abrechnungsdatum bearbeiten' : 'Vereinbarung bearbeiten')}
              </button>`
            : nothing}
        ${billingDraft?.taskID !== task._id ? billingStatus(task) : nothing}
        ${hasWorkTaskPayment(task)
          ? html`<p class="zw-task-help">
              ${t('Vor einer Änderung der Vereinbarung zuerst die Zahlungsmarkierung ausdrücklich aufheben.')}
            </p>`
          : nothing}
      </section>`;
    let billingReport: WorkTaskBillingReport | null = null;
    let billingRangeError = '';
    try {
      billingReport = buildWorkTaskBillingReport(tasks, billingFrom, billingTo);
    } catch {
      billingRangeError = t('Bitte wählen Sie einen gültigen Zeitraum. „Bis“ darf nicht vor „Von“ liegen.');
    }
    const exportBilling = async () => {
      if (actions.billingBusy || actions.busy || draft || !data || data.truncated || !billingReport?.rows.length)
        return;
      actions.billingBusy = true;
      setBillingBusy(true);
      setError(null);
      setNotice(null);
      try {
        const exported = parseJSON<{ filename: string; mime: string; base64: string }>(
          await workTaskBillingExport(billingFrom, billingTo)(),
        );
        if (!actions.alive) return;
        if (exported.mime !== 'application/pdf' || !exported.base64 || !exported.filename)
          throw new Error('INVALID_PDF');
        const bytes = Uint8Array.from(atob(exported.base64), character => character.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = exported.filename.replace(/[^a-zA-Z0-9._-]/g, '_');
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
        setNotice('Abrechnungsbericht als PDF erstellt. Die Tasks wurden nicht als abgerechnet markiert.');
      } catch (cause) {
        if (actions.alive) setError(explainError(cause));
      } finally {
        actions.billingBusy = false;
        if (actions.alive) setBillingBusy(false);
      }
    };
    let draftTotal = '—';
    if (draft) {
      try {
        draftTotal = formatTaskHours(estimateTotal(draftEstimate(draft)));
      } catch {
        /* Incomplete input remains editable. */
      }
    }

    const taskDetails = (task: WorkTask) =>
      html` <article
        class="zw-task-card"
        aria-label=${viewTask(task).title}
        data-priority=${task.priority ?? 'medium'}
        data-pinned=${task.pinned ? 'true' : 'false'}
      >
        <div class="zw-task-card-topline">
          <span class="zw-task-priority" data-priority=${task.priority ?? 'medium'}
            ><span aria-hidden="true">●</span> ${t(
              PRIORITIES.find(item => item.value === (task.priority ?? 'medium'))?.label ?? 'Medium',
            )}
            · ${t('Priorität')}</span
          >
          ${canManage
            ? html`<button
                type="button"
                class="zw-task-pin"
                aria-label=${task.pinned
                  ? `${language === 'en' ? 'Unpin task' : 'Task lösen'}: ${viewTask(task).title}`
                  : `${language === 'en' ? 'Pin task' : 'Task anheften'}: ${viewTask(task).title}`}
                aria-pressed=${task.pinned ? 'true' : 'false'}
                ?disabled=${busy !== null || draft !== null || billingDraft !== null}
                @click=${() => organize(task, task.priority ?? 'medium', !task.pinned)}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="15"
                  height="15"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.7"
                  aria-hidden="true"
                >
                  <path d="M8 3h8M9 3v6l-3 4v2h12v-2l-3-4V3M12 15v6" /></svg
                >${task.pinned ? t('Angeheftet') : t('Anheften')}
              </button>`
            : task.pinned
              ? html`<span class="zw-task-help">${t('Angeheftet')}</span>`
              : nothing}
        </div>
        <div class="zw-task-card-main">
          <div class="zw-task-card-tags">
            ${task.archivedAt ? html`<span class="zw-task-archived-pill">${t('Archiviert')}</span>` : nothing}
            <span class="zw-task-status-pill" data-status=${task.status}
              >${t(STATUSES.find(status => status.value === task.status)?.label ?? task.status)}</span
            ><span class="zw-task-kind-pill" data-kind=${task.kind}>${t(kindLabel(task.kind))}</span>
            ${!isApproved(task) &&
            task.kind === 'feature' &&
            (task.status === 'raised' || task.status === 'acknowledged')
              ? html`<span class="zw-task-approval-pill" data-approved="false">${t('Freigabe ausstehend')}</span>`
              : nothing}
          </div>
          <div class="zw-task-title-row">
            <span class="zw-task-number">${task.taskNumber}</span>
            <h2>${viewTask(task).title}</h2>
          </div>
          ${translationState(task)}
          <div class="zw-task-responsibility">
            <span>${t('Verantwortlich')}: <strong>${task.assignee?.name ?? t('Nicht zugewiesen')}</strong></span
            ><span>${nextAction(task)}</span>
          </div>
          <section
            class="zw-task-test-guide"
            data-testing=${task.status === 'test-staging' ? 'true' : 'false'}
            aria-label=${t('Testanleitung')}
          >
            <h3>${t('Testanleitung')}</h3>
            <p>${viewTask(task).testInstructions || t('Noch keine Testanleitung hinterlegt.')}</p>
          </section>
          ${viewTask(task).description.length > 220
            ? html`<details class="zw-task-description-details">
                <summary>
                  <span>${t('Beschreibung & Umfang')}</span
                  ><span class="zw-task-description-preview">${viewTask(task).description.slice(0, 220)}…</span>
                </summary>
                <p class="zw-task-description">${viewTask(task).description}</p>
              </details>`
            : html`<p
                class="zw-task-description"
                .textContent=${viewTask(task).description || t('Der Umfang wird noch ergänzt.')}
              ></p>`}
          <div class="zw-task-plan-metrics" role="group" aria-label=${t('Planung')}>
            <div class="zw-task-delivery">
              <span class="zw-task-metric-label"
                ><svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  aria-hidden="true"
                >
                  <rect x="3" y="5" width="18" height="16" rx="2" />
                  <path d="M7 2v6M17 2v6M3 11h18" /></svg
                >${t('Target release')}</span
              >
              <strong class="zw-task-release-value" data-unset=${task.targetReleaseDate ? 'false' : 'true'}
                >${calendarDate(task.targetReleaseDate)}</strong
              >
            </div>
            <div class="zw-task-duration">
              <span class="zw-task-metric-label">${t('Voraussichtliche Dauer')}</span>
              <strong>${viewTask(task).deliveryEstimate || t('Noch nicht festgelegt')}</strong>
            </div>
            <aside class="zw-task-card-estimate" aria-label=${t('Stundenschätzung')}>
              <div class="zw-task-hours-title">${t('Geschätzter Aufwand')}</div>
              <div class="zw-task-total" data-open=${taskEstimate(task).mode === 'open' ? 'true' : 'false'}>
                ${formatTaskHours(taskTotal(task))}
              </div>
              ${estimateDetails(task)}
            </aside>
          </div>
          ${billingSection(task)}
          ${task.feedbackLinks.length
            ? html`<details class="zw-task-linked-feedback">
                <summary>
                  ${task.feedbackLinks.length}
                  ${task.feedbackLinks.length === 1 ? t('verknüpftes Feedback') : t('verknüpfte Feedbacks')}
                  <span aria-hidden="true">↗</span>
                </summary>
                <div>${viewTask(task).feedbackLinks.map(sourceCard)}</div>
              </details>`
            : html`<span class="zw-task-independent">${t('Noch kein Feedback verknüpft')}</span>`}
          ${canManage
            ? html`<button
                type="button"
                class="zw-task-add-feedback"
                ?disabled=${busy !== null || draft !== null || billingDraft !== null}
                @click=${() => {
                  setAttachID(attachID === task._id ? null : task._id);
                  setAttachValue('');
                }}
              >
                ${t('+ Feedback hinzufügen')}
              </button>`
            : nothing}
          ${attachID === task._id && canManage
            ? html`<form class="zw-task-attach" @submit=${(event: Event) => attachFeedback(task, event)}>
                <label for=${`zw-task-attach-${task._id}`}
                  >${t('Anruf-ID oder Link aus diesem System')}<input
                    id=${`zw-task-attach-${task._id}`}
                    required
                    maxlength="2048"
                    .value=${attachValue}
                    ?disabled=${busy !== null || draft !== null || billingDraft !== null}
                    @input=${(event: Event) => setAttachValue((event.currentTarget as HTMLInputElement).value)}
                /></label>
                <p class="zw-task-help">
                  ${t('Der Anruf muss Feedback enthalten.')}
                  ${isApproved(task) ? t(' Eine neue Verknüpfung setzt die Freigabe zurück.') : ''}
                </p>
                <button
                  type="submit"
                  class="zw-task-button"
                  ?disabled=${busy !== null || draft !== null || !attachValue.trim()}
                >
                  ${t('Verknüpfen')}
                </button>
              </form>`
            : nothing}
        </div>

        <footer class="zw-task-card-footer">
          <div class="zw-task-approval-meta">
            ${isApproved(task) && task.approval
              ? html`<span class="zw-task-approved-icon" aria-hidden="true">✓</span
                  ><span
                    ><strong>${t('Freigegeben von')} ${task.approval.byName}</strong
                    ><small>${date(task.approval.at)} · ${t('Planung')} ${task.approval.estimateRevision}</small></span
                  >`
              : html`<span
                  ><strong
                    >${canEstimateBeApproved(task)
                      ? task.kind === 'bug'
                        ? t('Bug · nicht kostenpflichtig')
                        : t('Bereit zur Freigabe')
                      : task.kind === 'unclassified'
                        ? t('Klassifikation noch offen')
                        : t('Planung noch unvollständig')}</strong
                  ><small
                    >${canEstimateBeApproved(task)
                      ? task.kind === 'bug'
                        ? t('Freigabe optional. Fortschritt manuell aktualisieren.')
                        : t('Die Zustimmung von Michael oder Herrn Brenner dokumentieren.')
                      : t('Klassifikation, Beschreibung und gegebenenfalls Schätzung ergänzen.')}</small
                  ></span
                >`}
          </div>
          <div class="zw-task-card-actions">
            <button
              type="button"
              class="zw-task-button"
              ?disabled=${exportProgress !== null || busy !== null || draft !== null || billingDraft !== null}
              @click=${() => void copyAnalysis(task)}
            >
              ${t('Task für Analyse kopieren')}
            </button>
            ${canManage
              ? html`<select
                    class="zw-task-status-select"
                    data-status=${task.status}
                    aria-label=${`${language === 'en' ? 'Stage for' : 'Status für'} ${viewTask(task).title}`}
                    ?disabled=${busy !== null || draft !== null || billingDraft !== null}
                    @change=${(event: Event) => changeStatus(task, event)}
                  >
                    ${STATUSES.map(
                      status =>
                        html`<option value=${status.value} .selected=${status.value === task.status}>
                          ${t(status.label)}
                        </option>`,
                    )}</select
                  ><button
                    type="button"
                    class="zw-task-button"
                    ?disabled=${busy !== null || draft !== null || billingDraft !== null}
                    @click=${() => openDraft(task)}
                  >
                    ${t('Bearbeiten')}
                  </button>`
              : nothing}
            ${canManage
              ? task.archivedAt
                ? html`<button
                    type="button"
                    class="zw-task-button zw-task-restore-button"
                    ?disabled=${busy !== null || draft !== null || billingDraft !== null}
                    @click=${() => setArchived(task, false)}
                  >
                    ${t('Wiederherstellen')}
                  </button>`
                : html`<button
                    type="button"
                    class="zw-task-button zw-task-archive-button"
                    ?disabled=${busy !== null ||
                    draft !== null ||
                    billingDraft !== null ||
                    (commercialMinutes(task) > 0 && !hasWorkTaskPayment(task))}
                    @click=${() => {
                      setArchiveID(task._id);
                      setApproveID(null);
                    }}
                  >
                    ${t('Archivieren')}
                  </button>`
              : nothing}
            ${canManage && !task.archivedAt && commercialMinutes(task) > 0 && !hasWorkTaskPayment(task)
              ? html`<span class="zw-task-help">${t('Archivieren erst nach Zahlungseingang.')}</span>`
              : nothing}
            ${canApprove && !isApproved(task)
              ? html`<button
                  type="button"
                  class="zw-task-button zw-task-approve"
                  ?disabled=${busy !== null || !canEstimateBeApproved(task) || draft !== null || billingDraft !== null}
                  @click=${() => {
                    setApproveID(task._id);
                    setApproveName('');
                    setError(null);
                  }}
                >
                  ${t('Freigabe erfassen')}
                </button>`
              : nothing}
            ${busy === task._id ? html`<span class="zw-task-help" role="status">${t('Speichert…')}</span>` : nothing}
          </div>
        </footer>
        ${approveID === task._id && canApprove && !isApproved(task)
          ? html`<section class="zw-task-confirm" aria-label=${t('Freigabe bestätigen')}>
              <div>
                <h3>${t('Freigabe dokumentieren')}</h3>
                <p>
                  ${t(kindLabel(task.kind))} · ${t('Beschriebener Umfang:')}
                  <strong>${formatTaskHours(taskTotal(task))}</strong>${t(
                    'inklusive Testing. Target release:',
                  )}<strong>${calendarDate(task.targetReleaseDate)}</strong>.
                </p>
                <label class="zw-task-approver-field" for=${'zw-task-approver-' + task._id}
                  >${t('Freigegeben von')}<select
                    id=${'zw-task-approver-' + task._id}
                    .value=${approveName}
                    ?disabled=${busy !== null}
                    @change=${(event: Event) =>
                      setApproveName((event.currentTarget as HTMLSelectElement).value as '' | 'Michael' | 'Brenner')}
                  >
                    <option value="">${t('Bitte auswählen…')}</option>
                    <option value="Michael">Michael</option>
                    <option value="Brenner">Brenner</option>
                  </select>
                </label>
                <p class="zw-task-help">
                  ${t(
                    'Erfassen Sie, wer die Planung freigegeben hat. Ihr angemeldetes Konto und der Zeitpunkt werden zusätzlich protokolliert.',
                  )}
                </p>
              </div>
              <div class="zw-task-confirm-actions">
                <button
                  type="button"
                  class="zw-task-button"
                  ?disabled=${busy !== null}
                  @click=${() => setApproveID(null)}
                >
                  ${t('Abbrechen')}</button
                ><button
                  type="button"
                  class="zw-task-button zw-task-approve"
                  ?disabled=${busy !== null || !approveName}
                  @click=${() =>
                    approveName &&
                    void mutate(
                      task._id,
                      () => workTaskApprove(task._id, task.revision, approveName)(),
                      t('Freigabe dokumentiert. Status: Approved.'),
                      () => setApproveID(null),
                    )}
                >
                  ${busy === task._id ? t('Speichert…') : t('Freigabe speichern')}
                </button>
              </div>
            </section>`
          : nothing}
        ${archiveID === task._id && !task.archivedAt && canManage
          ? html`<section class="zw-task-confirm zw-task-archive-confirm" aria-label=${t('Archivierung bestätigen')}>
              <div>
                <h3>${t('Task archivieren?')}</h3>
                <p>
                  ${t(
                    'Der Task verschwindet aus dieser Liste. Angaben, Stunden, Termine und Feedback bleiben erhalten. Im Archiv können Sie ihn wiederherstellen.',
                  )}
                </p>
              </div>
              <div class="zw-task-confirm-actions">
                <button
                  type="button"
                  class="zw-task-button zw-task-archive-cancel"
                  ?disabled=${busy !== null}
                  @click=${() => setArchiveID(null)}
                >
                  ${t('Abbrechen')}
                </button>
                <button
                  type="button"
                  class="zw-task-button zw-task-archive-submit"
                  ?disabled=${busy !== null || draft !== null || billingDraft !== null}
                  @click=${() => setArchived(task, true)}
                >
                  ${t('Archivieren')}
                </button>
              </div>
            </section>`
          : nothing}
        <div class="zw-task-updated zw-task-dates">
          <span>${t('Erstellt')} ${date(task.createdAt)}</span>
          ${task.productionCompletion
            ? html`<span>${t('Produktion bestätigt am')} ${date(task.productionCompletion.at)}</span>`
            : nothing}
          ${task.completedAt && !task.productionCompletion
            ? html`<span
                >${t('Frühere Abschlussmarkierung (keine Produktionsbestätigung)')} ${date(task.completedAt)}</span
              >`
            : nothing}
          ${task.archivedAt ? html`<span>${t('Archiviert am')} ${date(task.archivedAt)}</span>` : nothing}
          <span>${t('Aktualisiert')} ${date(task.updatedAt)}</span>
        </div>
      </article>`;

    return html`${workTaskStyles}
      <section class="zw-tasks" lang=${language} aria-label=${t('Geplante Arbeiten und Freigaben')}>
        <div class="zw-task-language-bar">
          <div class="zw-task-language-toggle" role="group" aria-label=${t('Sprache der Tasks')}>
            <button
              id="zw-tasks-language-de"
              type="button"
              aria-pressed=${language === 'de' ? 'true' : 'false'}
              @click=${() => setLanguage('de')}
            >
              Deutsch
            </button>
            <button
              id="zw-tasks-language-en"
              type="button"
              aria-pressed=${language === 'en' ? 'true' : 'false'}
              @click=${() => setLanguage('en')}
            >
              English
            </button>
          </div>
          ${language === 'en'
            ? html`<small>${t('Englische Ansicht · Originaldaten bleiben unverändert')}</small>`
            : nothing}
        </div>
        <header class="zw-task-page-head">
          <div>
            <p class="zw-task-eyebrow">${t('Gemeinsam weiterentwickeln')}</p>
            <h1>${t('Tasks & Abrechnung')}</h1>
            <p class="zw-task-intro">
              ${t(
                'Von der Beobachtung zur Umsetzung. Aufgaben planen, Arbeitsstunden abstimmen und Fortschritte gemeinsam verfolgen.',
              )}
            </p>
          </div>
          <div class="zw-task-head-actions">
            <button
              type="button"
              class="zw-task-button"
              ?disabled=${!data || draft !== null || busy !== null || exportProgress !== null}
              @click=${() => void copyAnalysis()}
            >
              ${t('Alles für Analyse kopieren')}
            </button>
            ${canManage
              ? html`<button
                  type="button"
                  class="zw-task-button zw-task-primary"
                  ?disabled=${busy !== null || draft !== null || billingDraft !== null || linkSource !== null}
                  @click=${() => openDraft()}
                >
                  <span aria-hidden="true">+</span>${t('Neuer Task')}
                </button>`
              : nothing}
          </div>
        </header>
        <div class="zw-task-summary" aria-label=${t('Task-Übersicht')}>
          <div>
            <span>${t('Offene Tasks')}</span><strong>${data ? openTasks.length : '—'}</strong
            ><small>${t('Geplant und in Umsetzung')}</small>
          </div>
          <div class="zw-task-summary-wait">
            <span>${t('Warten auf Freigabe')}</span
            ><strong
              >${data ? openTasks.filter(task => task.kind === 'feature' && !isApproved(task)).length : '—'}</strong
            ><small>${t('Planungsfreigabe · keine Abrechnungsvereinbarung')}</small>
          </div>
          <div class="zw-task-summary-hours">
            <div class="zw-task-hours-totals">${hoursSummaryMetric()}</div>
            <small class="zw-task-hours-note">
              ${t('Nur ausdrücklich vereinbarte Beträge · keine automatische Rechnungs- oder Zahlungsbestätigung')}
            </small>
          </div>
        </div>
        <nav class="zw-task-views" aria-label=${t('Task-Bereiche')}>
          ${(
            [
              [
                'active',
                layout === 'board' ? 'Alle aktuellen Tasks' : 'Aktiv',
                layout === 'board' ? tasks.length - archivedTasks.length : openTasks.length,
              ],
              ['completed', 'Abgeschlossen', completedTasks.length],
              ['archived', 'Archiv', archivedTasks.length],
            ] as const
          ).map(
            ([view, label, count]) =>
              html`<button
                id=${'zw-task-view-' + view}
                type="button"
                aria-pressed=${taskView === view ? 'true' : 'false'}
                ?disabled=${!data || busy !== null || draft !== null || billingDraft !== null}
                @click=${() => selectTaskView(view)}
              >
                ${t(label)}<span>${data ? count : '—'}</span>
              </button>`,
          )}
        </nav>
        <p class="zw-task-view-help">
          ${t(
            taskView === 'active'
              ? 'Freigabe zur Veröffentlichung und Abschluss in Produktion sind getrennte Schritte.'
              : taskView === 'completed'
                ? 'Abgeschlossene Tasks bleiben mit allen Angaben erhalten und können bei Bedarf wieder geöffnet werden.'
                : 'Archivierte Tasks bleiben erhalten. Beim Wiederherstellen gilt wieder ihr bisheriger Status.',
          )}
        </p>
        ${error
          ? html`<div class="zw-task-alert" role="alert">
              <span>${t(error)}</span
              ><button
                type="button"
                class="zw-task-button"
                ?disabled=${busy !== null || loading}
                @click=${() => void loadTasks()}
              >
                ${t('Neu laden')}
              </button>
            </div>`
          : nothing}
        ${exportProgress
          ? html`<div class="zw-task-export-state" role="status">
              <span>${t(exportProgress)}</span>
              <button
                type="button"
                class="zw-task-button"
                @click=${() => {
                  actions.exportCancelled = true;
                  setExportProgress('Export wird abgebrochen…');
                }}
              >
                ${t('Abbrechen')}
              </button>
            </div>`
          : nothing}
        ${exportText
          ? html`<div class="zw-task-export-state">
              <span>${t('Feedback, Task und Gespräch / Logs · ohne Stunden')}</span>
              <div>
                <button
                  type="button"
                  class="zw-task-button"
                  @click=${async () => {
                    try {
                      await navigator.clipboard.writeText(exportText);
                      setNotice('Analysepaket kopiert.');
                    } catch {
                      setError('Kopieren ist hier nicht verfügbar. Das Analysepaket kann als TXT gespeichert werden.');
                    }
                  }}
                >
                  ${t('Text kopieren')}
                </button>
                <button type="button" class="zw-task-button" @click=${() => downloadWorkTaskAnalysis(exportText)}>
                  ${t('Als TXT speichern')}
                </button>
              </div>
            </div>`
          : nothing}
        ${notice
          ? html`<div class="zw-task-notice" role="status"><span aria-hidden="true">✓</span> ${t(notice)}</div>`
          : nothing}
        <details class="zw-task-billing-panel">
          <summary>
            <span class="zw-task-billing-icon" aria-hidden="true">↗</span>
            <span
              ><strong>${t('Abrechnung & PDF')}</strong
              ><small>${t('Features nach Abrechnungsdatum zusammenstellen')}</small></span
            >
            <span class="zw-task-billing-summary"
              >${billingReport
                ? `${billingReport.rows.length} ${billingReport.rows.length === 1 ? 'Feature' : 'Features'} · ${formatMinutes(billingReport.totalMinutes)}`
                : t('Zeitraum prüfen')}</span
            >
            <span class="zw-task-billing-chevron" aria-hidden="true"></span>
          </summary>
          <div class="zw-task-billing-content">
            ${language === 'en'
              ? html`<p class="zw-task-editor-language-note">
                  ${t(
                    'Der PDF-Bericht wird auf Deutsch mit Originaltiteln erstellt. Analysepakete enthalten die ursprünglichen Belege.',
                  )}
                </p>`
              : nothing}
            <div class="zw-task-billing-controls">
              <label for="zw-task-billing-from"
                >${t('Von')}<input
                  id="zw-task-billing-from"
                  type="date"
                  .value=${billingFrom}
                  ?disabled=${billingBusy}
                  @input=${(event: Event) => setBillingFrom((event.currentTarget as HTMLInputElement).value)}
              /></label>
              <label for="zw-task-billing-to"
                >${t('Bis einschließlich')}<input
                  id="zw-task-billing-to"
                  type="date"
                  .value=${billingTo}
                  ?disabled=${billingBusy}
                  @input=${(event: Event) => setBillingTo((event.currentTarget as HTMLInputElement).value)}
              /></label>
              <button
                type="button"
                class="zw-task-button zw-task-primary"
                data-action="billing-export"
                ?disabled=${!data ||
                data.truncated === true ||
                billingBusy ||
                busy !== null ||
                draft !== null ||
                !billingReport?.rows.length}
                @click=${() => void exportBilling()}
              >
                ${billingBusy ? t('PDF wird erstellt…') : t('Abrechnungs-PDF herunterladen')}
              </button>
            </div>
            <p class="zw-task-help">
              ${t(
                'Im PDF: ausdrücklich kostenpflichtig vereinbarte Features ab Implemented mit Abrechnungsdatum. Bugs und kostenlose Features sind ausgeschlossen. Die Stundensumme oben gilt dagegen für alle vereinbarten Tasks.',
              )}
            </p>
            ${billingRangeError
              ? html`<p class="zw-task-billing-error" role="alert">${billingRangeError}</p>`
              : nothing}
            ${data?.truncated
              ? html`<p class="zw-task-billing-error" role="alert">
                  ${t(
                    'Die Task-Liste ist unvollständig. Für eine verlässliche Vorschau und den PDF-Export müssen alle Tasks geladen sein.',
                  )}
                </p>`
              : nothing}
            ${billingReport
              ? html` ${billingReport.rows.length
                    ? html`<div class="zw-task-billing-table-wrap">
                        <table class="zw-task-billing-table">
                          <caption class="zw-task-sr-only">
                            ${t('Abrechenbare Features im gewählten Zeitraum')}
                          </caption>
                          <thead>
                            <tr>
                              <th scope="col">Task</th>
                              <th scope="col">${t('Abrechnungsdatum')}</th>
                              <th scope="col">Status</th>
                              <th scope="col">${t('Finale Stunden')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${billingReport.rows.map(
                              row =>
                                html`<tr>
                                  <td>
                                    <span class="zw-task-number">${row.taskNumber}</span
                                    ><strong>${viewTask(tasks.find(item => item._id === row.id)!).title}</strong>
                                  </td>
                                  <td>${calendarDate(row.billingDate)}</td>
                                  <td>${t(statusLabel(row.status))}</td>
                                  <td>${formatMinutes(row.billingMinutes)}</td>
                                </tr>`,
                            )}
                          </tbody>
                          <tfoot>
                            <tr>
                              <th scope="row" colspan="3">
                                ${billingReport.rows.length} ${billingReport.rows.length === 1 ? 'Feature' : 'Features'}
                                · ${t('Gesamt')}
                              </th>
                              <td>${formatMinutes(billingReport.totalMinutes)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>`
                    : html`<p class="zw-task-billing-empty">
                        ${t('Für diesen Zeitraum liegen noch keine abrechenbaren Features vor.')}
                      </p>`}
                  <div class="zw-task-billing-exclusions" aria-label=${t('Nicht im Bericht enthalten')}>
                    ${billingReport.counts.missingHours
                      ? html`<span>${billingReport.counts.missingHours} ${t('ohne finale Stunden')}</span>`
                      : nothing}
                    ${billingReport.counts.missingBillingDate
                      ? html`<span>${billingReport.counts.missingBillingDate} ${t('ohne Abrechnungsdatum')}</span>`
                      : nothing}
                    ${billingReport.counts.notApproved
                      ? html`<span>${billingReport.counts.notApproved} ${t('ohne Abrechnungsvereinbarung')}</span>`
                      : nothing}
                    ${billingReport.counts.notImplemented
                      ? html`<span>${billingReport.counts.notImplemented} ${t('noch nicht implementiert')}</span>`
                      : nothing}
                    ${billingReport.counts.bugs
                      ? html`<span>${billingReport.counts.bugs} ${t('Bugs · nicht kostenpflichtig')}</span>`
                      : nothing}
                    ${billingReport.counts.unclassified
                      ? html`<span>${billingReport.counts.unclassified} ${t('noch nicht zugeordnet')}</span>`
                      : nothing}
                    ${billingReport.counts.outsidePeriod
                      ? html`<span>${billingReport.counts.outsidePeriod} ${t('außerhalb des Zeitraums')}</span>`
                      : nothing}
                    ${billingReport.counts.invalidData
                      ? html`<span>${billingReport.counts.invalidData} ${t('mit unvollständigen Aufgabendaten')}</span>`
                      : nothing}
                  </div>`
              : nothing}
            <p class="zw-task-help">
              ${t(
                'Der PDF-Bericht berücksichtigt auch abgeschlossene und archivierte Features nach denselben Abrechnungsregeln.',
              )}
            </p>
            <p class="zw-task-billing-footnote">
              ${t(
                'Der PDF-Bericht ist eine Abrechnungsgrundlage mit Stunden, kein Rechnungsbeleg. Ein erneuter Export markiert oder berechnet keine Tasks automatisch.',
              )}
            </p>
          </div>
        </details>
        ${linkSource && canManage
          ? html`<section class="zw-task-link-panel" aria-labelledby="zw-task-link-title">
              <div class="zw-task-editor-head">
                <div>
                  <p class="zw-task-eyebrow">${t('Beobachtung zuordnen')}</p>
                  <h2 id="zw-task-link-title">${t('Feedback mit einem Task verknüpfen')}</h2>
                </div>
                <button
                  type="button"
                  class="zw-task-button"
                  ?disabled=${busy !== null}
                  @click=${() => setLinkSource(null)}
                >
                  ${t('Schließen')}
                </button>
              </div>
              ${sourceCard(linkSource)}
              <div class="zw-task-link-controls">
                <label for="zw-task-link-target"
                  >${t('Vorhandener Task')}<select
                    id="zw-task-link-target"
                    .value=${linkTarget}
                    ?disabled=${busy !== null}
                    @change=${(event: Event) => setLinkTarget((event.currentTarget as HTMLSelectElement).value)}
                  >
                    <option value="">${t('Task auswählen…')}</option>
                    ${tasks.map(
                      task =>
                        html`<option
                          value=${task._id}
                          ?disabled=${task.feedbackLinks.some(source => source.callID === linkSource.callID)}
                        >
                          ${task.taskNumber} ·
                          ${viewTask(task).title}${task.feedbackLinks.some(
                            source => source.callID === linkSource.callID,
                          )
                            ? t(' · bereits verknüpft')
                            : ''}
                        </option>`,
                    )}
                  </select></label
                ><button
                  type="button"
                  class="zw-task-button zw-task-primary"
                  ?disabled=${!linkTarget || busy !== null}
                  @click=${submitLink}
                >
                  ${busy === linkTarget ? t('Verknüpft…') : t('Feedback verknüpfen')}
                </button>
              </div>
              <p class="zw-task-help">
                ${t(
                  'Die ursprüngliche Beobachtung bleibt erhalten. Bei einem bereits freigegebenen Task erfordert die zusätzliche Verknüpfung eine neue Freigabe.',
                )}
              </p>
              ${!tasks.length
                ? html`<p class="zw-task-help">
                    ${t('Noch keine Tasks vorhanden. Erstellen Sie zuerst einen neuen Task.')}
                  </p>`
                : nothing}
            </section>`
          : nothing}
        ${draft && canManage
          ? html`<form id="zw-task-editor" class="zw-task-editor" novalidate @submit=${saveDraft}>
              <div class="zw-task-editor-head">
                <div>
                  <p class="zw-task-eyebrow">${t('Arbeitsplanung')}</p>
                  <h2>${draft._id ? t('Task bearbeiten') : t('Neuen Task erstellen')}</h2>
                </div>
                <span class="zw-task-draft-label">${draft._id ? t('Planung aktualisieren') : t('Neuer Entwurf')}</span>
              </div>
              <div class="zw-task-form-layout">
                ${language === 'en'
                  ? html`<p class="zw-task-editor-language-note">
                        ${t('Bearbeitungsfelder enthalten den Originaltext. Übersetzungen dienen nur zur Ansicht.')}
                      </p>
                      ${draft._id && data?.tasks.find(item => item._id === draft._id)
                        ? translationState(data.tasks.find(item => item._id === draft._id)!)
                        : nothing}`
                  : nothing}
                <fieldset id="zw-task-kind" class="zw-task-kind-choice">
                  <legend>${t('Klassifikation *')}</legend>
                  <div class="zw-task-kind-options">
                    ${(['bug', 'feature'] as const).map(
                      kind =>
                        html` <label
                          class="zw-task-kind-option"
                          data-selected=${draft.kind === kind ? 'true' : 'false'}
                          for=${`zw-task-kind-${kind}`}
                        >
                          <input
                            id=${`zw-task-kind-${kind}`}
                            name="zw-task-kind"
                            type="radio"
                            value=${kind}
                            .checked=${draft.kind === kind}
                            ?disabled=${busy !== null}
                            @change=${() => setDraft({ ...draft, kind })}
                          />
                          <span
                            ><strong>${t(kindLabel(kind))}</strong
                            ><small
                              >${t(
                                kind === 'bug'
                                  ? 'Fehler beheben · ohne Kundenberechnung'
                                  : 'Neue Funktion · kostenlos oder nach Vereinbarung',
                              )}</small
                            ></span
                          >
                        </label>`,
                    )}
                  </div>
                  ${draft.kind === 'unclassified'
                    ? html`<p class="zw-task-help">${t('Bitte Bug oder Feature auswählen.')}</p>`
                    : nothing}
                  ${draft.kind !== 'feature' && (draft.billingHours.trim() || draft.billingDate)
                    ? html`<p class="zw-task-kind-billing-warning" role="status">
                        <strong>${t('Abrechnungsdaten werden beim Speichern entfernt.')}</strong>${t(
                          'Finale abrechenbare Stunden und Abrechnungsdatum gelten nur für Features. Wenn Sie vor dem Speichern wieder Feature wählen, bleiben Ihre Eingaben erhalten.',
                        )}
                      </p>`
                    : nothing}
                </fieldset>
                <div class="zw-task-editor-core">
                  <section class="zw-task-form-main" aria-labelledby="zw-task-basics-title">
                    <div class="zw-task-section-heading">
                      <span>01</span>
                      <h3 id="zw-task-basics-title">${t('Aufgabe')}</h3>
                      ${draft.taskNumber ? html`<span class="zw-task-number">${draft.taskNumber}</span>` : nothing}
                    </div>
                    <label for="zw-task-title"
                      >${t('Titel *')}<input
                        id="zw-task-title"
                        required
                        maxlength="180"
                        placeholder=${t('Was soll verbessert oder umgesetzt werden?')}
                        .value=${draft.title}
                        ?disabled=${busy !== null}
                        @input=${(event: Event) =>
                          updateDraft('title', (event.currentTarget as HTMLInputElement).value)}
                    /></label>
                    ${draftTranslation('title')}
                    <label for="zw-task-description"
                      >${t('Beschreibung & Umfang')}<textarea
                        id="zw-task-description"
                        rows="4"
                        maxlength="8000"
                        placeholder=${t('Beobachtung, gewünschte Änderung und Abnahmekriterien…')}
                        .value=${draft.description}
                        ?disabled=${busy !== null}
                        @input=${(event: Event) =>
                          updateDraft('description', (event.currentTarget as HTMLTextAreaElement).value)}
                      ></textarea>
                    </label>
                    ${draftTranslation('description')}
                  </section>
                  <section class="zw-task-form-group zw-task-estimate-form" aria-labelledby="zw-task-estimate-title">
                    <div class="zw-task-section-heading">
                      <span>02</span>
                      <h3 id="zw-task-estimate-title">${t('Aufwand schätzen')}</h3>
                    </div>
                    <label for="zw-task-estimate-mode"
                      >${t('Schätzung')}<select
                        id="zw-task-estimate-mode"
                        ?disabled=${busy !== null}
                        @change=${(event: Event) =>
                          setDraft({
                            ...draft,
                            estimateMode: (event.currentTarget as HTMLSelectElement).value as WorkTaskEstimate['mode'],
                          })}
                      >
                        ${[
                          { value: 'total', label: t('Gesamtaufwand') },
                          { value: 'breakdown', label: t('Nach Tätigkeiten') },
                          { value: 'open', label: t('Noch offen') },
                        ].map(
                          mode =>
                            html`<option value=${mode.value} .selected=${mode.value === draft.estimateMode}>
                              ${t(mode.label)}
                            </option>`,
                        )}
                      </select></label
                    >
                    <p class="zw-task-help">
                      ${t('Eine Gesamtzahl genügt. Spanne und Aufteilung nach Tätigkeiten sind optional.')}
                    </p>
                    ${draft.estimateMode === 'breakdown'
                      ? html`${hourInput('research', t('Recherche'), 'researchHours', 'researchMax')}${hourInput(
                          'development',
                          t('Entwicklung'),
                          'developmentHours',
                          'developmentMax',
                        )}${hourInput('testing', 'Testing', 'testingHours', 'testingMax')}`
                      : draft.estimateMode === 'total'
                        ? hourInput('total', t('Gesamt inklusive Testing'), 'totalMin', 'totalMax')
                        : html`<p class="zw-task-open-note">
                            ${t('Die Schätzung bleibt offen und wird nicht als 0 Stunden gewertet.')}
                          </p>`}
                    <div class="zw-task-total-preview">
                      <span>${t('Geschätzter Aufwand')}</span><strong>${draftTotal}</strong>
                    </div>
                  </section>
                </div>
                <details id="zw-task-planning-details" class="zw-task-editor-details">
                  <summary>
                    <strong>${t('Terminplanung')}</strong
                    ><span class="zw-task-detail-summary"
                      >${owners.get(draft.assigneeUserID) ?? t('Nicht zugewiesen')} ·
                      ${t(draft.priority === 'high' ? 'High' : draft.priority === 'low' ? 'Low' : 'Medium')} ·
                      ${calendarDate(draft.targetReleaseDate || null)}</span
                    >
                  </summary>
                  <div class="zw-task-details-body">
                    <label for="zw-task-assignee"
                      >${t('Verantwortlich')}<select
                        id="zw-task-assignee"
                        ?disabled=${busy !== null}
                        @change=${(event: Event) =>
                          updateDraft('assigneeUserID', (event.currentTarget as HTMLSelectElement).value)}
                      >
                        <option value="" .selected=${!draft.assigneeUserID}>${t('Nicht zugewiesen')}</option>
                        ${draft.assigneeUserID && !data?.assignees?.some(owner => owner.userID === draft.assigneeUserID)
                          ? html`<option value=${draft.assigneeUserID} selected>
                              ${owners.get(draft.assigneeUserID) ?? t('Bisherige Zuordnung')} ·
                              ${t('Konto nicht aktiv')}
                            </option>`
                          : nothing}
                        ${(data?.assignees ?? []).map(
                          owner =>
                            html`<option value=${owner.userID} .selected=${draft.assigneeUserID === owner.userID}>
                              ${owner.name}
                            </option>`,
                        )}
                      </select></label
                    >
                    <label for="zw-task-priority"
                      >${t('Priorität')}<select
                        id="zw-task-priority"
                        ?disabled=${busy !== null}
                        @change=${(event: Event) =>
                          setDraft({
                            ...draft,
                            priority: (event.currentTarget as HTMLSelectElement).value as WorkTaskPriority,
                          })}
                      >
                        ${PRIORITIES.map(
                          priority =>
                            html`<option value=${priority.value} .selected=${priority.value === draft.priority}>
                              ${t(priority.label)}
                            </option>`,
                        )}
                      </select></label
                    >
                    <div class="zw-task-schedule-heading">
                      <h3>${t('Terminplanung')}</h3>
                      <p class="zw-task-help">
                        ${t('Kalendertermin und Dauer sind unabhängig vom geschätzten Arbeitsaufwand.')}
                      </p>
                    </div>
                    <label for="zw-task-target-release"
                      >${t('Target release')}<input
                        id="zw-task-target-release"
                        type="date"
                        min="2000-01-01"
                        max="9999-12-31"
                        .value=${draft.targetReleaseDate}
                        ?disabled=${busy !== null}
                        @input=${(event: Event) =>
                          updateDraft('targetReleaseDate', (event.currentTarget as HTMLInputElement).value)}
                    /></label>
                    <p class="zw-task-help">
                      ${t('Optional. Ohne Datum steht auf der Karte „Noch nicht festgelegt“.')}
                    </p>
                    <label for="zw-task-delivery"
                      >${t('Erwarteter Lieferzeitraum')}<input
                        id="zw-task-delivery"
                        maxlength="500"
                        placeholder=${t('z. B. 2–3 Arbeitstage nach Freigabe')}
                        .value=${draft.deliveryEstimate}
                        ?disabled=${busy !== null}
                        @input=${(event: Event) =>
                          updateDraft('deliveryEstimate', (event.currentTarget as HTMLInputElement).value)}
                    /></label>
                    ${draftTranslation('deliveryEstimate')}
                    <p class="zw-task-help">
                      ${t('Für Bugs und Features: die erwartete Kalenderdauer, zum Beispiel 2–3 Arbeitstage.')}
                    </p>

                    <label class="zw-task-pin-option"
                      ><input
                        type="checkbox"
                        .checked=${draft.pinned}
                        ?disabled=${busy !== null}
                        @change=${(event: Event) =>
                          setDraft({ ...draft, pinned: (event.currentTarget as HTMLInputElement).checked })}
                      />${t('Oben anheften')}</label
                    >
                  </div>
                </details>
                <details id="zw-task-testing-details" class="zw-task-editor-details">
                  <summary>
                    <strong>${t('Testanleitung')}</strong
                    ><span class="zw-task-detail-summary"
                      >${t(
                        draft.testInstructions.trim()
                          ? 'Testanleitung vorhanden'
                          : 'Optional: Schritte und erwartetes Ergebnis',
                      )}
                      · ${draft.feedbackLinks.length} ${t('Feedback & Anrufe')}</span
                    >
                  </summary>
                  <div class="zw-task-details-body">
                    <div class="zw-task-test-editor">
                      <label for="zw-task-test-instructions"
                        >${t('Testanleitung')}<textarea
                          id="zw-task-test-instructions"
                          rows="6"
                          maxlength="8000"
                          placeholder=${t('Voraussetzungen / Testkonto, Schritte und erwartetes Ergebnis…')}
                          .value=${draft.testInstructions}
                          ?disabled=${busy !== null}
                          @input=${(event: Event) =>
                            updateDraft('testInstructions', (event.currentTarget as HTMLTextAreaElement).value)}
                        ></textarea>
                      </label>
                      <p class="zw-task-help">
                        ${t('Im Staging-Test sichtbar. Anleitung und Zuordnung ändern weder Umfang noch Freigabe.')}
                      </p>
                      ${draftTranslation('testInstructions')}
                    </div>
                    <details class="zw-task-form-sources">
                      <summary>${t('Feedback & Anrufe')}<span>${draft.feedbackLinks.length}</span></summary>
                      <div>
                        ${draft.feedbackLinks.length
                          ? draftFeedback().map(sourceCard)
                          : html`<p class="zw-task-help">
                              ${t('Feedback kann nach dem Speichern aus der Feedback-Liste zugeordnet werden.')}
                            </p>`}
                      </div>
                    </details>
                  </div>
                </details>
                <p class="zw-task-help">
                  ${t(
                    'Abrechnungsvereinbarung und Zahlung werden separat in den Task-Details verwaltet. Diese Bearbeitung verändert sie nicht.',
                  )}
                </p>
              </div>
              ${draft.wasApproved
                ? html`<p class="zw-task-approval-change">
                    ${t(
                      'Änderungen an Titel, Umfang, Klassifikation, Schätzung oder Target release setzen den Status auf Acknowledged und erfordern eine neue Freigabe. Finale Abrechnungsdaten, Priorität und Anheften bleiben davon unabhängig.',
                    )}
                  </p>`
                : nothing}
              <footer class="zw-task-editor-footer">
                <span>${t('Planungsfreigabe, Abrechnungsvereinbarung und Zahlung werden separat dokumentiert.')}</span>
                <div>
                  <button
                    type="button"
                    class="zw-task-button"
                    ?disabled=${busy !== null}
                    @click=${() => setDraft(null)}
                  >
                    ${t('Abbrechen')}</button
                  ><button
                    type="submit"
                    class="zw-task-button zw-task-primary"
                    ?disabled=${busy !== null || !draft.title.trim() || (!draft._id && draft.kind === 'unclassified')}
                  >
                    ${busy === (draft._id ?? 'new') ? t('Speichert…') : t('Task speichern')}
                  </button>
                </div>
              </footer>
            </form>`
          : nothing}
        <div class="zw-task-board-controls">
          <div class="zw-task-language-toggle" role="group" aria-label=${t('Darstellung')}>
            <button
              id="zw-task-view-board"
              type="button"
              aria-pressed=${layout === 'board' ? 'true' : 'false'}
              @click=${() => setLayout('board')}
            >
              ${t('Board')}
            </button>
            <button
              id="zw-task-view-list"
              type="button"
              aria-pressed=${layout === 'list' ? 'true' : 'false'}
              @click=${() => setLayout('list')}
            >
              ${t('Liste')}
            </button>
          </div>
          <p>${t('Karte öffnen für Planung, Testanleitung und Aktionen.')}</p>
        </div>
        <details class="zw-task-filter-panel">
          <summary>
            ${t('Suche & Filter')}<span
              >${[
                search.trim(),
                statusFilter !== 'all',
                kindFilter !== 'all',
                approvalFilter !== 'all',
                priorityFilter !== 'all',
                ownerFilter !== 'all',
                releaseFilter !== 'all',
              ].filter(Boolean).length || ''}</span
            >
          </summary>
          <div class="zw-task-toolbar">
            <label class="zw-task-search"
              ><span class="zw-task-sr-only">${t('Tasks durchsuchen')}</span
              ><svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                aria-hidden="true"
              >
                <circle cx="10" cy="10" r="6.5" />
                <path d="m15 15 5 5" /></svg
              ><input
                type="search"
                placeholder=${t('Task-ID, Titel, Feedback oder Anruf…')}
                .value=${search}
                @input=${(event: Event) => setSearch((event.currentTarget as HTMLInputElement).value)}
            /></label>
            <label
              ><span class="zw-task-sr-only">${t('Arbeitsstatus filtern')}</span
              ><select
                aria-label=${t('Arbeitsstatus filtern')}
                .value=${statusFilter}
                @change=${(event: Event) => setStatusFilter((event.currentTarget as HTMLSelectElement).value)}
              >
                <option value="all">${t('Alle Status')}</option>
                ${STATUSES.map(status => html`<option value=${status.value}>${t(status.label)}</option>`)}
              </select></label
            >
            <label
              ><span class="zw-task-sr-only">${t('Klassifikation filtern')}</span
              ><select
                aria-label=${t('Klassifikation filtern')}
                @change=${(event: Event) => setKindFilter((event.currentTarget as HTMLSelectElement).value)}
              >
                <option value="all" .selected=${kindFilter === 'all'}>${t('Alle Klassifikationen')}</option>
                ${KINDS.map(
                  kind =>
                    html`<option value=${kind.value} .selected=${kindFilter === kind.value}>${t(kind.label)}</option>`,
                )}
              </select></label
            >
            <label
              ><span class="zw-task-sr-only">${t('Freigabe filtern')}</span
              ><select
                aria-label=${t('Freigabe filtern')}
                .value=${approvalFilter}
                @change=${(event: Event) => setApprovalFilter((event.currentTarget as HTMLSelectElement).value)}
              >
                <option value="all">${t('Alle Freigaben')}</option>
                <option value="pending">${t('Warten auf Freigabe')}</option>
                <option value="approved">${t('Freigegeben')}</option>
              </select></label
            >
            <label
              ><span class="zw-task-sr-only">${t('Priorität filtern')}</span
              ><select
                aria-label=${t('Priorität filtern')}
                .value=${priorityFilter}
                @change=${(event: Event) => setPriorityFilter((event.currentTarget as HTMLSelectElement).value)}
              >
                <option value="all">${t('Alle Prioritäten')}</option>
                ${PRIORITIES.map(priority => html`<option value=${priority.value}>${t(priority.label)}</option>`)}
              </select></label
            >
            <label
              ><span class="zw-task-sr-only">${t('Verantwortliche filtern')}</span
              ><select
                aria-label=${t('Verantwortliche filtern')}
                .value=${ownerFilter}
                @change=${(event: Event) => setOwnerFilter((event.currentTarget as HTMLSelectElement).value)}
              >
                <option value="all">${t('Alle Verantwortlichen')}</option>
                <option value="mine">${t('Mir zugewiesen')}</option>
                <option value="unassigned">${t('Nicht zugewiesen')}</option>
                ${[...owners].map(([id, name]) => html`<option value=${id}>${name}</option>`)}
              </select></label
            >
            <label
              ><span class="zw-task-sr-only">${t('Zieldatum filtern')}</span
              ><select
                aria-label=${t('Zieldatum filtern')}
                .value=${releaseFilter}
                @change=${(event: Event) => setReleaseFilter((event.currentTarget as HTMLSelectElement).value)}
              >
                <option value="all">${t('Alle Termine')}</option>
                <option value="overdue">${t('Überfällig')}</option>
                <option value="unset">${t('Ohne Zieldatum')}</option>
              </select></label
            >
            <button
              type="button"
              class="zw-task-button"
              data-action="reset-filters"
              @click=${() => {
                setSearch('');
                setStatusFilter('all');
                setKindFilter('all');
                setPriorityFilter('all');
                setApprovalFilter('all');
                setOwnerFilter('all');
                setReleaseFilter('all');
              }}
            >
              ${t('Filter zurücksetzen')}
            </button>
            <button
              type="button"
              class="zw-task-button"
              ?disabled=${loading || busy !== null}
              @click=${() => void loadTasks()}
            >
              ${loading ? t('Lädt…') : t('Aktualisieren')}
            </button>
          </div>
        </details>
        ${data?.truncated
          ? html`<p class="zw-task-help" style="margin-top:12px">
              ${t(
                'Bis zu 1.000 Tasks werden angezeigt; Suche und Filter beziehen sich auf die geladenen Karten. Die abrechenbaren Stunden werden über alle Tasks berechnet.',
              )}
            </p>`
          : nothing}
        <p class="zw-task-filter-scope">
          ${t(
            'Filter gelten für Karten und Spaltenzähler. Abrechenbare Stunden oben: alle Tasks, auch abgeschlossen oder archiviert. PDF: eigener Zeitraum.',
          )}
        </p>
        <div class="zw-task-list-caption">
          <span>${data ? `${shown.length} ${shown.length === 1 ? 'Task' : 'Tasks'}` : t('Tasks werden geladen…')}</span
          ><span>${t('Freigaben: Michael / Herr Brenner · von angemeldeten Nutzern dokumentiert')}</span>
        </div>
        ${loading && !data
          ? html`<div class="zw-task-empty" role="status">${t('Tasks werden geladen…')}</div>`
          : !data
            ? nothing
            : !shown.length
              ? html`<div class="zw-task-empty">
                  <span class="zw-task-empty-symbol" aria-hidden="true">✓</span>
                  <h2>
                    ${t(
                      viewTasks.length
                        ? 'Keine passenden Tasks'
                        : taskView === 'archived'
                          ? 'Das Archiv ist leer'
                          : taskView === 'completed'
                            ? 'Noch keine abgeschlossenen Tasks'
                            : 'Keine aktiven Tasks',
                    )}
                  </h2>
                  <p>
                    ${t(
                      viewTasks.length
                        ? 'Passen Sie die Suche oder die Filter an.'
                        : taskView === 'archived'
                          ? 'Nicht mehr benötigte Tasks können Sie über „Archivieren“ hier ablegen.'
                          : taskView === 'completed'
                            ? 'Nur ausdrücklich als in Produktion abgeschlossen markierte Tasks erscheinen hier.'
                            : 'Erstellen Sie einen Task direkt oder übernehmen Sie eine Beobachtung aus dem Feedback.',
                    )}
                  </p>
                  ${canManage && !tasks.length && taskView === 'active'
                    ? html`<button
                        type="button"
                        class="zw-task-button zw-task-primary"
                        ?disabled=${draft !== null || busy !== null}
                        @click=${() => openDraft()}
                      >
                        ${t('Ersten Task erstellen')}
                      </button>`
                    : nothing}
                </div>`
              : layout === 'board'
                ? html`<div class="zw-task-board" role="region" aria-label=${t('Aufgaben nach Status')} tabindex="0">
                    ${(taskView === 'completed'
                      ? STATUSES.filter(stage => stage.value === 'production-completed')
                      : STATUSES
                    )
                      .filter(stage => statusFilter === 'all' || stage.value === statusFilter)
                      .map(stage => {
                        const items = shown.filter(task => task.status === stage.value);
                        return html`<section
                          class="zw-task-column"
                          data-stage=${stage.value}
                          aria-label=${t(stage.label)}
                        >
                          <header>
                            <h2>${t(stage.label)}</h2>
                            <span>${items.length}</span>
                          </header>
                          <div class="zw-task-column-cards">
                            ${items.map(
                              task =>
                                html`<button
                                  type="button"
                                  class="zw-task-compact-card"
                                  data-priority=${task.priority ?? 'medium'}
                                  aria-label=${`${t('Details öffnen')}: ${viewTask(task).title}`}
                                  ?disabled=${draft !== null || busy !== null}
                                  @click=${() => {
                                    setError(null);
                                    setNotice(null);
                                    setDetailID(task._id);
                                  }}
                                >
                                  <span class="zw-task-compact-top"
                                    ><span class="zw-task-number">${task.taskNumber}</span>${task.pinned
                                      ? html`<span title=${t('Angeheftet')}>◆</span>`
                                      : nothing}</span
                                  >
                                  <span class="zw-task-kind-pill" data-kind=${task.kind}
                                    >${t(kindLabel(task.kind))}</span
                                  >
                                  <strong class="zw-task-compact-title">${viewTask(task).title}</strong>
                                  <span class="zw-task-compact-billing" aria-label=${t('Abrechenbare Zeit')}>
                                    <span class="zw-task-compact-billing-label">${t('Abrechenbare Zeit')}</span>
                                    <strong class="zw-task-compact-billing-value"
                                      >${commercialMinutes(task)
                                        ? formatMinutes(commercialMinutes(task))
                                        : task.kind === 'bug' || task.billingDisposition === 'non-billable'
                                          ? t('Nicht kostenpflichtig')
                                          : t('Noch nicht vereinbart')}</strong
                                    >
                                    ${commercialMinutes(task)
                                      ? html`<span
                                          class="zw-task-compact-payment"
                                          data-paid=${hasWorkTaskPayment(task) ? 'true' : 'false'}
                                        >
                                          ${hasWorkTaskPayment(task) ? '✓ ' + t('Bezahlt') : t('Noch nicht bezahlt')}
                                        </span>`
                                      : nothing}
                                  </span>
                                  <span
                                    class="zw-task-compact-date"
                                    data-overdue=${task.targetReleaseDate &&
                                    task.targetReleaseDate < today &&
                                    !task.archivedAt &&
                                    task.status !== 'production-completed'
                                      ? 'true'
                                      : 'false'}
                                    ><span>${t('Target release')}</span
                                    ><strong>${calendarDate(task.targetReleaseDate)}</strong></span
                                  >
                                  <span class="zw-task-compact-owner"
                                    ><span class="zw-task-owner-avatar" aria-hidden="true"
                                      >${task.assignee
                                        ? task.assignee.name.slice(0, 2).toLocaleUpperCase(language)
                                        : '—'}</span
                                    ><span>${task.assignee?.name ?? t('Nicht zugewiesen')}</span></span
                                  >
                                  ${task.status === 'test-staging'
                                    ? html`<span class="zw-task-test-link"
                                        >${task.testInstructions
                                          ? t('Testanleitung öffnen')
                                          : t('Testanleitung fehlt')}</span
                                      >`
                                    : nothing}
                                  <span class="zw-task-compact-next">${nextAction(task)}</span>
                                </button>`,
                            )}${!items.length
                              ? html`<p class="zw-task-column-empty">${t('Keine Tasks in diesem Schritt')}</p>`
                              : nothing}
                          </div>
                        </section>`;
                      })}
                  </div>`
                : html`<div class="zw-task-list">${shown.map(taskDetails)}</div>`}
        ${detailID && tasks.find(task => task._id === detailID)
          ? html`<dialog
              id="zw-task-detail-dialog"
              class="zw-task-detail-dialog"
              aria-label=${t('Aufgabe')}
              @cancel=${(event: Event) => {
                event.preventDefault();
                if (!actions.busy) closeDetails();
              }}
            >
              <header class="zw-task-dialog-heading">
                <span>${t('Aufgabe')}</span
                ><button
                  autofocus
                  type="button"
                  class="zw-task-button"
                  ?disabled=${busy !== null}
                  @click=${closeDetails}
                >
                  ${t('Schließen')} ×
                </button>
              </header>
              ${error ? html`<p class="zw-task-dialog-error" role="alert">${t(error)}</p>` : nothing}
              ${notice ? html`<p class="zw-task-dialog-notice" role="status">${t(notice)}</p>` : nothing}
              ${taskDetails(tasks.find(task => task._id === detailID)!)}
            </dialog>`
          : nothing}
      </section>`;
  },
);
